import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabaseClient';
import LoadingSpinner from './LoadingSpinner';
import AgentCreatorModal from './AgentCreatorModal'; // Modal antigo (conversacional)
import AgentCreatorStepModal from './AgentCreatorStepModal'; // Modal novo (step-by-step)
import './AgentsPage.css';
import { API_ENDPOINTS, BACKEND_NODE_URL } from '../config/constants';
import { useAuth } from '../context/AuthContext';
import AppIcon from './icons/AppIcon';

/**
 * AgentsPage - Reconstruída do zero
 * Estrutura limpa e organizada para gerenciamento de agentes
 * 
 * IMPORTANTE: Esta página é exibida no menu como "Agentes Halo" (label invertido)
 * Rota: /meus-agentes
 * Label no menu: "Agentes Halo"
 * Função: CONFIGURAÇÃO/CRIAÇÃO de agentes (Halo Tools 1.0)
 */
export default function AgentsPage() {
  const { user } = useAuth();

  // ============================================================================
  // ESTADOS
  // ============================================================================

  // Lista de agentes
  const [agents, setAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [errorAgents, setErrorAgents] = useState(null);

  // Agente selecionado e navegação
  const [agenteSelecionado, setAgenteSelecionado] = useState(null);
  const [abaAtiva, setAbaAtiva] = useState('perfil');
  const [busca, setBusca] = useState('');

  // Redimensionamento da sidebar
  const containerRef = useRef(null);
  const [sidebarWidth, setSidebarWidth] = useState(null); // px
  const [isResizing, setIsResizing] = useState(false);

  // Ref para controle de clique duplo no modal
  const step1PostingRef = useRef(false);

  // Modal de criação (novo modal conversacional)
  const [novoModalAberto, setNovoModalAberto] = useState(false);
  const [agenteParaEditar, setAgenteParaEditar] = useState(null);
  
  // Modal de criação (antigo - mantido para compatibilidade)
  const [modalAberto, setModalAberto] = useState(false);
  const [modelChoice, setModelChoice] = useState(null); // null | 'nano' | 'pro' | 'webhook'
  const [haloStep, setHaloStep] = useState(1); // 1-4 para Halo Tools e Nano

  // Formulário Halo Tools (modal)
  const [haloForm, setHaloForm] = useState({
    nome: '',
    descricao: '',
    canal: 'Interno',
    whatsappConnectionId: '',
    funcaoPrincipal: '', // 'atendimento' | 'suporte_interno'
    subFuncao: '', // depende da funcaoPrincipal
    temperatura: 0.7,
    modeloIa: 'gpt-4o-mini',
    wait_text: '',
    personalidade: '',
    contexto: '',
    direcionamento: '',
    restricoes: '',
    objetivos: '',
    informacoesGerais: '',
    descricaoInstrucao: '',
    agenteEnabled: true,
    agruparEnabled: false,
    agruparSegundos: '',
  });

  // Estados para Etapa 1 (conexões WhatsApp)
  const [haloStep1Loading, setHaloStep1Loading] = useState(false);
  const [haloStep1Error, setHaloStep1Error] = useState('');
  const [whatsappConnections, setWhatsappConnections] = useState([]);

  // Estados para Etapa 3 (ferramentas)
  const [haloToolChoice, setHaloToolChoice] = useState(''); // 'produtos' | 'formulario'
  const [loadingCategorias, setLoadingCategorias] = useState(false);
  const [errorCategorias, setErrorCategorias] = useState('');
  const [categoriasDisponiveis, setCategoriasDisponiveis] = useState([]);
  const [categoriasSelecionadas, setCategoriasSelecionadas] = useState([]);
  
  // Estados para formulários CRM
  const [loadingFormularios, setLoadingFormularios] = useState(false);
  const [errorFormularios, setErrorFormularios] = useState('');
  const [formulariosDisponiveis, setFormulariosDisponiveis] = useState([]);
  const [formulariosSelecionados, setFormulariosSelecionados] = useState([]);
  
  // Estados para projetos
  const [projetosDisponiveis, setProjetosDisponiveis] = useState([]);

  // Estados para salvamento de infraestrutura
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');

  // Estados para Base de Conhecimento
  const [knowledgeDocs, setKnowledgeDocs] = useState([]);
  const [loadingKnowledge, setLoadingKnowledge] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addType, setAddType] = useState('url'); // 'url' | 'file'
  const [urlInput, setUrlInput] = useState('');
  const [fileInput, setFileInput] = useState(null);
  const [addingKnowledge, setAddingKnowledge] = useState(false);
  const [addError, setAddError] = useState('');
  const [knowledgeRefresh, setKnowledgeRefresh] = useState(0);

  // Estados para edição de Ferramentas
  const [editandoCatalogos, setEditandoCatalogos] = useState(false);
  const [catalogosTemp, setCatalogosTemp] = useState([]);
  const [enableWhatsappCatalog, setEnableWhatsappCatalog] = useState(false);

  // Estados para Etapa 4 (salvamento)
  const [haloSaving, setHaloSaving] = useState(false);
  const [haloSaveError, setHaloSaveError] = useState('');
  const [haloSaveSuccess, setHaloSaveSuccess] = useState('');
  const [isRefreshingAfterCreate, setIsRefreshingAfterCreate] = useState(false);

  // Estados para Geração com IA (Etapa 2)
  const [showAiGeneratorModal, setShowAiGeneratorModal] = useState(false);
  const [aiGeneratorLoading, setAiGeneratorLoading] = useState(false);
  const [aiGeneratorError, setAiGeneratorError] = useState('');
  const [aiDescricaoFuncao, setAiDescricaoFuncao] = useState('');

  // Formulário de edição/criação (agentes normais)
  const [formData, setFormData] = useState({
    nome: '',
    canal: 'WhatsApp',
    whatsappConnectionId: '',
    tipo: 'Atendimento',
    descricao: '',
    temperatura: 0.7,
    funcaoPrincipal: '',
    subFuncao: '',
    personalidade: '',
    contexto: '',
    direcionamento: '',
    restricoes: '',
    objetivos: '',
    informacoesGerais: '',
    descricaoInstrucao: '',
    modeloIa: 'gpt-4o-mini',
    wait_text: '',
    agenteEnabled: true,
    agruparEnabled: false,
    agruparSegundos: '',
  });

  // Estados de salvamento (já declarados acima com isSaving)
  const [saving, setSaving] = useState(false);

  // ============================================================================
  // FUNÇÕES AUXILIARES
  // ============================================================================

  /**
   * Busca o company_id do usuário logado
   */
  const getCompanyId = () => {
    try {
      const stored = localStorage.getItem('company_id');
      if (stored) return stored;
      if (user?.user_metadata?.company_id) return user.user_metadata.company_id;
      if (user?.company_id) return user.company_id;
      return null;
    } catch {
      return null;
    }
  };

  /**
   * Carrega lista de agentes do Supabase
   */
  const carregarAgentes = async () => {
    try {
      setLoadingAgents(true);
      setErrorAgents(null);

      const companyId = getCompanyId();
      if (!companyId) {
        throw new Error('Empresa não identificada. Faça login novamente.');
      }

      const { data, error } = await supabase
        .from('agentes')
        .select('id, nome, descricao, canal, funcao_principal, modelo, temperatura, conexao_id, base_prompt, sign_messages, read_messages, typing_delay_seconds, chatbot_enabled, chatbot_ignore_groups, chatbot_stop_when_you_send_msg, enable_tools, atualizado_em, criado_em')
        .eq('empresa_id', companyId)
        .order('atualizado_em', { ascending: false });

      if (error) throw error;

      setAgents(data || []);
    } catch (err) {
      console.error('Erro ao carregar agentes:', err);
      setErrorAgents(err.message || 'Falha ao carregar agentes');
    } finally {
      setLoadingAgents(false);
      setInitialLoading(false);
    }
  };

  /**
   * Seleciona um agente para edição
   */
  const selecionarAgente = (agente) => {
    setAgenteSelecionado(agente);
    
    // Define aba inicial baseada no tipo de agente
    const isHalo = isHaloAgent(agente);
    setAbaAtiva(isHalo ? 'etapa1' : 'perfil');
    
    // Dados serão carregados pelo useEffect
  };

  /**
   * Abre modal para criar novo agente (novo modal conversacional)
   */
  const abrirModalCriacao = () => {
    setAgenteParaEditar(null); // Limpar agente para edição
    setNovoModalAberto(true);
  };

  /**
   * Abre modal para editar agente existente
   */
  const abrirModalEdicao = (agente) => {
    setAgenteParaEditar(agente);
    setNovoModalAberto(true);
  };

  /**
   * Salva instruções do agente (Etapa 2)
   * ✅ Usa endpoint correto baseado no modelo do agente (Nano ou Pro/Tools)
   */
  const salvarInstrucoes = async () => {
    try {
      setSaving(true);
      setSaveError('');
      setSaveSuccess('');

      const companyId = getCompanyId();
      if (!companyId) throw new Error('Empresa não identificada.');
      if (!agenteSelecionado?.id) throw new Error('Nenhum agente selecionado.');

      const body = {
        company_id: companyId,
        agente_id: agenteSelecionado.id,
        sub_funcao: formData.subFuncao || null,
        personalidade: formData.personalidade || null,
        contexto: formData.contexto || null,
        direcionamento: formData.direcionamento || null,
        restricoes: formData.restricoes || null,
        objetivos: formData.objetivos || null,
        informacoes_gerais: formData.informacoesGerais || null,
        instrucoes_ferramentas: formData.instrucoesFerramentas || null,  // ✅ Campo de ferramentas
        descricao: formData.descricaoInstrucao || null,
      };

      // ✅ Escolher endpoint correto baseado no modelo do agente
      const modelo = (agenteSelecionado?.modelo || '').toString().trim().toLowerCase();
      const isNano = modelo === 'nano';
      const endpoint = isNano 
        ? `${API_ENDPOINTS.AGENTS_TOOLS_UPDATE.replace('/tools/', '/nano/')}` 
        : API_ENDPOINTS.AGENTS_TOOLS_UPDATE;

      console.log('[salvarInstrucoes] Modelo:', modelo, '| Endpoint:', endpoint);

      const resp = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err?.error || `Falha ao salvar instruções (${resp.status})`);
      }

      setSaveSuccess('Instruções salvas com sucesso!');
      await carregarAgentes();
      
    } catch (err) {
      console.error('Erro ao salvar instruções:', err);
      setSaveError(err.message || 'Falha ao salvar instruções');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Salva ferramentas do agente (Etapa 3)
   */
  const salvarFerramentas = async () => {
    try {
      setSaving(true);
      setSaveError('');
      setSaveSuccess('');

      const companyId = getCompanyId();
      if (!companyId) throw new Error('Empresa não identificada.');
      if (!agenteSelecionado?.id) throw new Error('Nenhum agente selecionado.');

      // Buscar dados atuais do agente para mesclar (não sobrescrever)
      const toolsPayload = {
        company_id: companyId,
        agente_id: agenteSelecionado.id,
        catalogos_ids: categoriasSelecionadas,
        formularios_ids: formulariosSelecionados,
        enable_whatsapp_catalog: enableWhatsappCatalog  // ✅ NOVO: Salvar flag do Carousel
      };

      const response = await fetch(API_ENDPOINTS.AGENTS_TOOLS_UPDATE_MAPPING, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(toolsPayload)
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err?.error || `Falha ao salvar ferramentas (${response.status})`);
      }

      const toolsCount = categoriasSelecionadas.length + formulariosSelecionados.length;
      setSaveSuccess(`${toolsCount} ferramenta(s) salva(s) com sucesso! ${enableWhatsappCatalog ? '📱 Carousel habilitado!' : ''}`);
      await carregarAgentes();
      
    } catch (err) {
      console.error('Erro ao salvar ferramentas:', err);
      setSaveError(err.message || 'Falha ao salvar ferramentas');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Salva configurações do agente (Etapa 4)
   */
  const salvarConfiguracoes = async () => {
    try {
      setSaving(true);
      setSaveError('');
      setSaveSuccess('');

      const companyId = getCompanyId();
      if (!companyId) throw new Error('Empresa não identificada.');
      if (!agenteSelecionado?.id) throw new Error('Nenhum agente selecionado.');

      const waitSeconds = (formData.agruparEnabled ? (parseInt(formData.agruparSegundos, 10) || 0) : 0);
      const isNano = (agenteSelecionado?.modelo || '').toString().trim().toLowerCase() === 'nano';
      
      // Chamar endpoint seguro no backend (que valida company_id)
      const endpoint = isNano 
        ? `${BACKEND_NODE_URL}/agents/nano/update-instructions`
        : `${BACKEND_NODE_URL}/agents/tools/update-instructions`;
      
      const body = {
        company_id: companyId,
        agente_id: agenteSelecionado.id,
        temperatura: formData.temperatura,
        habilitar_agente: !!formData.agenteEnabled,
        wait: waitSeconds,
        conexao_id: formData.whatsappConnectionId || null,
      };

      const resp = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err?.error || `Falha ao salvar (${resp.status})`);
      }

      setSaveSuccess('Configurações salvas com sucesso!');
      await carregarAgentes();
      
    } catch (err) {
      console.error('Erro ao salvar configurações:', err);
      setSaveError(err.message || 'Falha ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Exclui o agente selecionado com limpeza completa
   */
  const excluirAgente = async () => {
    if (!agenteSelecionado) return;
    
    const confirmacao = window.confirm(
      `Tem certeza que deseja excluir o agente "${agenteSelecionado.nome}"?\n\nEsta ação não pode ser desfeita e irá:\n- Deletar o agente\n- Remover configurações relacionadas\n- Desvincular de conexões`
    );
    
    if (!confirmacao) return;

    try {
      setSaving(true);
      setSaveError('');

      // 1. Desvincular conexões
      const { error: connError } = await supabase
        .from('connections')
        .update({ 
          agente_id: null, 
          with_ai: false 
        })
        .eq('agente_id', agenteSelecionado.id);

      if (connError) {
        console.warn('Erro ao desvincular conexões:', connError);
      }

      // 2. Deletar agente (CASCADE vai limpar halo_nano e agentes_halo_tools)
      const { error } = await supabase
        .from('agentes')
        .delete()
        .eq('id', agenteSelecionado.id);

      if (error) throw error;

      setSaveSuccess('Agente excluído com sucesso!');
      setAgenteSelecionado(null);
      
      // Recarrega lista
      await carregarAgentes();
      
    } catch (err) {
      console.error('Erro ao excluir agente:', err);
      setSaveError(err.message || 'Falha ao excluir agente');
    } finally {
      setSaving(false);
    }
  };

  // ============================================================================
  // EFFECTS
  // ============================================================================

  /**
   * Carrega agentes ao montar componente
   */
  useEffect(() => {
    carregarAgentes();
  }, []);

  /**
   * Carrega largura salva da sidebar do localStorage
   */
  useEffect(() => {
    try {
      const saved = localStorage.getItem('agents_sidebar_width_px');
      if (saved) {
        const w = parseInt(saved, 10);
        if (!isNaN(w) && w > 0) setSidebarWidth(w);
      }
    } catch (_) {}
  }, []);

  /**
   * Carrega dados completos do agente quando selecionado
   */
  useEffect(() => {
    const carregarDadosAgente = async () => {
      try {
        if (!agenteSelecionado?.id) return;
        
        const empresaId = getCompanyId();
        if (!empresaId) return;

        // Buscar dados completos do agente
        const { data: agenteData, error: agenteError } = await supabase
          .from('agentes')
          .select('*')
          .eq('empresa_id', empresaId)
          .eq('id', agenteSelecionado.id)
          .single();

        if (agenteError || !agenteData) return;

        // Buscar dados de halo_tools ou halo_nano conforme o modelo
        const isHalo = isHaloAgent(agenteData);
        const isNano = (agenteData?.modelo || '').toString().trim().toLowerCase() === 'nano';
        let haloData = null;
        
        if (isHalo) {
          // Buscar na tabela correta conforme o modelo
          const tableName = isNano ? 'halo_nano' : 'agentes_halo_tools';
          
          const { data: haloToolsData, error: haloError } = await supabase
            .from(tableName)
            .select('*')
            .eq('empresa_id', empresaId)
            .eq('agente_id', agenteSelecionado.id)
            .maybeSingle();
          
          if (!haloError && haloToolsData) {
            haloData = haloToolsData;
          }
        }

        // Priorizar funcao_principal de halo_tools, senão pegar de agentes
        const funcaoPrincipalOrigem = haloData?.funcao_principal || agenteData.funcao_principal || '';
        
        // Mapear funcao_principal para o formato do select (minúsculo)
        let funcaoPrincipalMapeada = '';
        if (funcaoPrincipalOrigem) {
          const fp = funcaoPrincipalOrigem.toLowerCase();
          if (fp.includes('atendimento')) funcaoPrincipalMapeada = 'atendimento';
          else if (fp.includes('suporte') || fp.includes('interno')) funcaoPrincipalMapeada = 'suporte_interno';
        }

        // Preencher formulário com dados do agente + halo_tools
        const canalAgente = haloData?.canal || agenteData.canal || 'WhatsApp';
        // ✅ Usar conexao_id de agenteData (fonte de verdade), não de haloData
        const conexaoId = agenteData.conexao_id || '';
        
        // Buscar dados da empresa
        const { data: empresaData } = await supabase
          .from('configuracoes_empresa')
          .select('*')
          .eq('company_id', empresaId)
          .single();

        setFormData({
          nome: agenteData.nome || '',
          canal: canalAgente,
          whatsappConnectionId: conexaoId,
          conexao_id: conexaoId,
          tipo: funcaoPrincipalOrigem || 'Atendimento',
          descricao: haloData?.descricao || agenteData.descricao || '',
          temperatura: Number.isFinite(haloData?.temperatura) ? haloData.temperatura : (Number.isFinite(agenteData.temperatura) ? agenteData.temperatura : 0.7),
          funcao_principal: funcaoPrincipalOrigem,
          funcaoPrincipal: funcaoPrincipalMapeada,
          subFuncao: haloData?.sub_funcao || '',
          personalidade: haloData?.personalidade || '',
          contexto: haloData?.contexto || '',
          direcionamento: haloData?.direcionamento || '',
          restricoes: haloData?.restricoes || '',
          objetivos: haloData?.objetivos || '',
          informacoesGerais: haloData?.informacoes_gerais || '',
          instrucoesFerramentas: haloData?.instrucoes_ferramentas || '',  // ✅ Campo de ferramentas
          descricaoInstrucao: haloData?.descricao || '',
          modeloIa: 'gpt-4o-mini',
          wait_text: '',
          agenteEnabled: haloData?.habilitar_agente ?? agenteData.chatbot_enabled ?? true,
          agruparEnabled: (haloData?.wait && haloData.wait > 0) ?? false,
          agruparSegundos: haloData?.wait || '',
          empresa: empresaData,
          catalogos_ids: agenteData.catalogos_ids || [],
          campos_formulario: []
        });
        
        // Carregar conexões WhatsApp
        try {
          const { data: conns, error: connsError } = await supabase
            .from('connections')
            .select('id, name, number, type, status')
            .eq('company_id', empresaId)
            .eq('type', 'whatsapp')
            .order('created_at', { ascending: false });
          
          if (!connsError && conns) {
            setWhatsappConnections(conns);
          }
        } catch (e) {
          console.error('Erro ao carregar conexões WhatsApp:', e);
        }

        // Carregar projetos disponíveis
        try {
          const { data: projetos, error: projError } = await supabase
            .from('projects')
            .select('id, name')
            .eq('company_id', empresaId)
            .order('name');
          
          if (!projError && projetos) {
            setProjetosDisponiveis(projetos);
          }
        } catch (e) {
          console.error('Erro ao carregar projetos:', e);
        }

        // Carregar categorias vinculadas
        try {
          const { data: categoriasVinculadas, error: catError } = await supabase
            .from('agentes_categorias')
            .select('categoria_id')
            .eq('agente_id', agenteSelecionado.id);
          
          if (!catError && categoriasVinculadas) {
            const categoriasIds = categoriasVinculadas.map(c => c.categoria_id);
            setCategoriasSelecionadas(categoriasIds);
          }
        } catch (e) {
          console.error('Erro ao carregar categorias:', e);
        }

        // ✅ NOVO: Carregar flag enable_whatsapp_catalog da tabela halo_nano
        try {
          const { data: haloNano, error: nanoError } = await supabase
            .from('halo_nano')
            .select('enable_whatsapp_catalog')
            .eq('agente_id', agenteSelecionado.id)
            .eq('empresa_id', empresaId)
            .maybeSingle();
          
          if (!nanoError && haloNano) {
            setEnableWhatsappCatalog(haloNano.enable_whatsapp_catalog || false);
            console.log('[AgentsPage] enable_whatsapp_catalog carregado:', haloNano.enable_whatsapp_catalog);
          }
        } catch (e) {
          console.error('Erro ao carregar enable_whatsapp_catalog:', e);
        }

        // Formulário: centralizado no Projeto CRM (não carrega mais de ferramentas_halo_formulario)

        // Carregar dados da conexão
        if (conexaoId) {
          try {
            const { data: conexao, error: connError } = await supabase
              .from('connections')
              .select('id, name, number')
              .eq('id', conexaoId)
              .eq('company_id', empresaId)
              .maybeSingle();
            
            console.log('[AgentsPage] Carregando conexão:', { conexaoId, empresaId, conexao, connError });
            
            if (!connError && conexao) {
              console.log('[AgentsPage] Conexão carregada:', conexao.name);
              setFormData(prev => ({
                ...prev,
                conexao_id: conexao.id,
                conexao_nome: conexao.name,
                conexao_numero: conexao.number
              }));
            } else if (!conexao) {
              console.log('[AgentsPage] Conexão não encontrada, limpando formData');
              setFormData(prev => ({
                ...prev,
                conexao_id: null,
                conexao_nome: null,
                conexao_numero: null
              }));
            }
          } catch (e) {
            console.error('Erro ao carregar conexão:', e);
          }
        }

        // Carregar projeto vinculado
        try {
          const { data: projeto, error: projError } = await supabase
            .from('projects')
            .select('id, name')
            .eq('ai_agent_id', agenteSelecionado.id)
            .eq('company_id', empresaId)
            .maybeSingle();
          
          if (!projError && projeto) {
            setFormData(prev => ({
              ...prev,
              projeto_id: projeto.id,
              projeto_nome: projeto.name
            }));
          }
        } catch (e) {
          console.error('Erro ao carregar projeto:', e);
        }

        // Atualizar agente selecionado com dados completos
        setAgenteSelecionado(agenteData);
        
        // Log para debug
              } catch (err) {
        console.error('Erro ao carregar dados do agente:', err);
      }
    };

    carregarDadosAgente();
  }, [agenteSelecionado?.id]);

  /**
   * Carrega categorias disponíveis quando está na aba etapa7 (Catálogos)
   */
  useEffect(() => {
    const carregarCategoriasDisponiveis = async () => {
      
      if (abaAtiva !== 'etapa7' || !agenteSelecionado?.id) return;
      
      const companyId = getCompanyId();
      if (!companyId) return;
      
      try {
        setLoadingCategorias(true);
        
        // Buscar todas as categorias da empresa
        const { data: categorias, error } = await supabase
          .from('categorias_produtos')
          .select('id, nome, total_produtos')
          .eq('company_id', companyId)
          .order('nome');
        
        if (error) {
          console.error('[Ferramentas] Erro ao carregar categorias:', error);
          setErrorCategorias(error.message);
          return;
        }
        
        setCategoriasDisponiveis(categorias || []);
        
      } catch (e) {
        console.error('[Ferramentas] Erro ao carregar categorias:', e);
        setErrorCategorias(e.message);
      } finally {
        setLoadingCategorias(false);
      }
    };
    
    carregarCategoriasDisponiveis();
  }, [abaAtiva, agenteSelecionado?.id]);

  /**
   * Carrega conexões WhatsApp quando abre o modal ou seleciona WhatsApp
   */
  useEffect(() => {
    const carregarConexoesWhatsApp = async () => {
      if (!modalAberto || haloForm.canal !== 'WhatsApp') {
        setWhatsappConnections([]);
        return;
      }

      const companyId = getCompanyId();
      if (!companyId) return;

      try {
        setHaloStep1Loading(true);
        setHaloStep1Error('');

        const { data, error } = await supabase
          .from('connections')
          .select('id, name, number, status, whatsapp_api_status')
          .eq('company_id', companyId)
          .eq('type', 'whatsapp')
          .order('name', { ascending: true });

        if (error) throw error;

        setWhatsappConnections(data || []);
      } catch (err) {
        console.error('Erro ao carregar conexões WhatsApp:', err);
        setHaloStep1Error(err.message || 'Falha ao carregar conexões');
        setWhatsappConnections([]);
      } finally {
        setHaloStep1Loading(false);
      }
    };

    carregarConexoesWhatsApp();
  }, [modalAberto, haloForm.canal]);

  /**
   * Carrega conexões WhatsApp quando edita agente existente e seleciona WhatsApp
   */
  useEffect(() => {
    const carregarConexoesParaEdicao = async () => {
      if (!agenteSelecionado || formData.canal !== 'WhatsApp') {
        return;
      }

      const companyId = getCompanyId();
      if (!companyId) return;

      try {
        const { data, error } = await supabase
          .from('connections')
          .select('id, name, number, status, whatsapp_api_status')
          .eq('company_id', companyId)
          .eq('type', 'whatsapp')
          .order('name', { ascending: true });

        if (error) throw error;

        setWhatsappConnections(data || []);
      } catch (err) {
        console.error('Erro ao carregar conexões WhatsApp para edição:', err);
      }
    };

    carregarConexoesParaEdicao();
  }, [agenteSelecionado, formData.canal]);

  /**
   * Carrega categorias quando seleciona ferramenta Produtos (modal ou editor)
   */
  useEffect(() => {
    let abort = false;
    
    const loadCategorias = async () => {
      if (haloToolChoice !== 'produtos') {
        setCategoriasDisponiveis([]);
        return;
      }
      
      const companyId = getCompanyId();
      if (!companyId) return;
      
      setLoadingCategorias(true);
      setErrorCategorias('');
      
      try {
        // Buscar categorias ativas
        const { data: categorias, error: catError } = await supabase
          .from('categorias_produtos')
          .select('id, nome, slug, ativo')
          .eq('company_id', companyId)
          .eq('ativo', true)
          .order('ordem', { ascending: true });
        
        if (catError) throw catError;
        
        if (!categorias || categorias.length === 0) {
          if (!abort) setCategoriasDisponiveis([]);
          return;
        }
        
        // Contar produtos por categoria
        const { data: produtos, error: prodError } = await supabase
          .from('produtos_empresa')
          .select('id, categoria_id')
          .eq('company_id', companyId)
          .is('deleted_at', null)
          .in('categoria_id', categorias.map(c => c.id));
        
        if (prodError) throw prodError;
        
        // Contar produtos por categoria
        const contagemProdutos = {};
        (produtos || []).forEach(p => {
          if (p.categoria_id) {
            contagemProdutos[p.categoria_id] = (contagemProdutos[p.categoria_id] || 0) + 1;
          }
        });
        
        // Adicionar contagem às categorias
        const categoriasComContagem = categorias.map(cat => ({
          ...cat,
          total_produtos: contagemProdutos[cat.id] || 0
        }));
        
        // Filtrar apenas categorias com produtos
        const comProdutos = categoriasComContagem.filter(c => c.total_produtos > 0);
        
        if (!abort) {
          setCategoriasDisponiveis(comProdutos);
        }
      } catch (e) {
        console.error('[Categorias] Erro ao carregar:', e);
        if (!abort) setErrorCategorias(e.message || 'Erro ao carregar categorias');
      } finally {
        if (!abort) setLoadingCategorias(false);
      }
    };
    
    loadCategorias();
    
    return () => { abort = true; };
  }, [haloToolChoice]);

  /**
   * Carrega projetos CRM com formulário quando seleciona ferramenta Formulário
   */
  useEffect(() => {
    let abort = false;
    
    const loadFormularios = async () => {
      if (haloToolChoice !== 'formulario') {
        setFormulariosDisponiveis([]);
        return;
      }
      
      const companyId = getCompanyId();
      if (!companyId) return;
      
      setLoadingFormularios(true);
      setErrorFormularios('');
      
      try {
        // Buscar projetos CRM com formulário habilitado
        const { data: projetos, error } = await supabase
          .from('projects')
          .select('id, name, icon, color, settings')
          .eq('company_id', companyId)
          .eq('project_type', 'crm')
          .eq('is_archived', false)
          .order('name');
        
        if (error) throw error;
        
        // Filtrar apenas projetos com formulário habilitado
        const projetosComFormulario = (projetos || []).filter(p => 
          p.settings?.crm?.useForm === true && 
          p.settings?.crm?.formFields && 
          p.settings?.crm?.formFields.length > 0
        );
        
        if (!abort) {
          setFormulariosDisponiveis(projetosComFormulario);
        }
      } catch (e) {
        console.error('[Formulários CRM] Erro ao carregar:', e);
        if (!abort) setErrorFormularios(e.message || 'Erro ao carregar formulários');
      } finally {
        if (!abort) setLoadingFormularios(false);
      }
    };
    
    loadFormularios();
    
    return () => { abort = true; };
  }, [haloToolChoice]);

  /**
   * Handlers de redimensionamento da sidebar
   */
  const onResizeMouseDown = (e) => {
    e.preventDefault();
    setIsResizing(true);
  };

  useEffect(() => {
    if (!isResizing) return;
    
    const onMouseMove = (e) => {
      if (!containerRef.current) return;
      const containerRect = containerRef.current.getBoundingClientRect();
      const newWidth = e.clientX - containerRect.left;
      const minW = 220;
      const maxW = containerRect.width * 0.5;
      setSidebarWidth(Math.max(minW, Math.min(maxW, newWidth)));
    };
    
    const onMouseUp = () => {
      if (isResizing) {
        setIsResizing(false);
        if (sidebarWidth) localStorage.setItem('agents_sidebar_width_px', String(Math.round(sidebarWidth)));
      }
    };
    
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [isResizing, sidebarWidth]);

  /**
   * Sincroniza catalogosTemp com categoriasSelecionadas
   */
  useEffect(() => {
    setCatalogosTemp([...categoriasSelecionadas]);
  }, [categoriasSelecionadas]);

  /**
   * Carrega documentos da base de conhecimento quando seleciona agente
   */
  useEffect(() => {
    const carregarKnowledge = async () => {
      if (!agenteSelecionado?.id || abaAtiva !== 'etapa6') return;
      
      try {
        setLoadingKnowledge(true);
        const response = await fetch(`${BACKEND_NODE_URL}/api/agents/knowledge/${agenteSelecionado.id}`);
        const data = await response.json();
        
        if (data.success) {
          setKnowledgeDocs(data.documents || data.data || []);
        }
      } catch (error) {
        console.error('Erro ao carregar knowledge:', error);
      } finally {
        setLoadingKnowledge(false);
      }
    };

    carregarKnowledge();
  }, [agenteSelecionado?.id, abaAtiva, knowledgeRefresh]);

  // ============================================================================
  // FUNÇÕES DE FERRAMENTAS (CATÁLOGOS)
  // ============================================================================

  const salvarCatalogos = async () => {
    try {
      setIsSaving(true);
      setSaveError('');

      const empresaId = getCompanyId();
      if (!empresaId || !agenteSelecionado?.id) {
        throw new Error('Empresa ou agente não identificado');
      }

      // 1. Deletar vínculos antigos
      const { error: deleteError } = await supabase
        .from('agentes_categorias')
        .delete()
        .eq('agente_id', agenteSelecionado.id);

      if (deleteError) throw new Error(`Erro ao deletar vínculos: ${deleteError.message}`);

      // 2. Inserir novos vínculos
      if (catalogosTemp.length > 0) {
        const vinculos = catalogosTemp.map(catId => ({
          company_id: empresaId,
          agente_id: agenteSelecionado.id,
          categoria_id: catId
        }));

        const { error: insertError } = await supabase
          .from('agentes_categorias')
          .insert(vinculos);

        if (insertError) throw new Error(`Erro ao criar vínculos: ${insertError.message}`);
      }

      // 3. Atualizar mapping no backend
      const payload = {
        company_id: empresaId,
        agente_id: agenteSelecionado.id,
        catalogos_ids: catalogosTemp,
        formularios_ids: formulariosSelecionados
      };

      const response = await fetch(API_ENDPOINTS.AGENTS_TOOLS_UPDATE_MAPPING, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err?.error || `Falha ao sincronizar ferramentas (${response.status})`);
      }

      setCategoriasSelecionadas(catalogosTemp);
      setAgenteSelecionado(prev => prev ? ({ ...prev, catalogos_ids: catalogosTemp }) : prev);
      setEditandoCatalogos(false);
      setSaveSuccess('✅ Catálogos salvos com sucesso!');
      
      setTimeout(() => setSaveSuccess(''), 3000);

    } catch (err) {
      console.error('[AgentsPage] Erro ao salvar catálogos:', err);
      setSaveError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  // ============================================================================
  // FUNÇÕES DE KNOWLEDGE BASE
  // ============================================================================

  const carregarKnowledge = async () => {
    if (!agenteSelecionado?.id) return;
    
    try {
      setLoadingKnowledge(true);
      const response = await fetch(`${BACKEND_NODE_URL}/api/agents/knowledge/${agenteSelecionado.id}`);
      const data = await response.json();
      
      if (data.success) {
        setKnowledgeDocs(data.documents || data.data || []);
      }
    } catch (error) {
      console.error('Erro ao carregar knowledge:', error);
    } finally {
      setLoadingKnowledge(false);
    }
  };

  const adicionarURL = async () => {
    try {
      setAddingKnowledge(true);
      setAddError('');

      const response = await fetch(`${BACKEND_NODE_URL}/api/agents/knowledge/add-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          agentId: agenteSelecionado.id,
          companyId: getCompanyId(),
          url: urlInput,
          userId: user?.id
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setShowAddModal(false);
        setUrlInput('');
        setKnowledgeRefresh(prev => prev + 1); // Força recarregamento
      } else {
        setAddError(data.error || 'Erro ao adicionar URL');
      }
    } catch (error) {
      setAddError(error.message);
    } finally {
      setAddingKnowledge(false);
    }
  };

  const adicionarArquivo = async () => {
    try {
      setAddingKnowledge(true);
      setAddError('');

      const formData = new FormData();
      formData.append('file', fileInput);
      formData.append('agentId', agenteSelecionado.id);
      formData.append('companyId', getCompanyId());
      formData.append('userId', user?.id);

      const response = await fetch(`${BACKEND_NODE_URL}/api/agents/knowledge/upload-file`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();
      
      if (data.success) {
        setShowAddModal(false);
        setFileInput(null);
        setKnowledgeRefresh(prev => prev + 1); // Força recarregamento
      } else {
        setAddError(data.error || 'Erro ao adicionar arquivo');
      }
    } catch (error) {
      setAddError(error.message);
    } finally {
      setAddingKnowledge(false);
    }
  };

  const deletarDocumento = async (docId) => {
    if (!confirm('Deseja realmente deletar este documento?')) return;

    try {
      const response = await fetch(`${BACKEND_NODE_URL}/api/agents/knowledge/${docId}`, {
        method: 'DELETE'
      });

      const data = await response.json();
      
      if (data.success) {
        setKnowledgeRefresh(prev => prev + 1); // Força recarregamento
      }
    } catch (error) {
      console.error('Erro ao deletar:', error);
    }
  };

  // ============================================================================
  // FUNÇÕES DE RENDERIZAÇÃO
  // ============================================================================

  /**
   * Retorna as iniciais do nome do agente
   */
  const getIniciais = (nome) => {
    if (!nome) return '?';
    const palavras = nome.trim().split(/\s+/);
    if (palavras.length === 1) return palavras[0].charAt(0).toUpperCase();
    return (palavras[0].charAt(0) + palavras[palavras.length - 1].charAt(0)).toUpperCase();
  };

  /**
   * Verifica se é agente Halo (Tools ou Nano)
   */
  const isHaloAgent = (agente) => {
    const modelo = (agente?.modelo || '').toString().trim().toLowerCase();
    return modelo === 'pro' || modelo === 'nano';
  };

  /**
   * Retorna o label do modelo do agente
   */
  const getAgentModelLabel = (ag) => {
    const modelo = (ag?.modelo || '').toString().trim().toLowerCase();
    if (modelo === 'pro') return 'Halo Pro 1.0';
    if (modelo === 'nano') return 'HALO Nano 1.0';
    if (modelo === 'conversacao') return 'Conversação';
    if (modelo === 'webhook') return 'Webhook';
    return 'Conversação';
  };

  /**
   * Monta o prompt consolidado a partir do formulário
   */
  const buildBasePrompt = (f) => {
    const linhas = [];
    linhas.push('personalidade:');
    linhas.push((f.personalidade || '').trim());
    linhas.push('');
    linhas.push('contexto:');
    linhas.push((f.contexto || '').trim());
    linhas.push('');
    linhas.push('direcionamento:');
    linhas.push((f.direcionamento || '').trim());
    linhas.push('');
    linhas.push('restricoes:');
    linhas.push((f.restricoes || '').trim());
    linhas.push('');
    linhas.push('objetivos:');
    linhas.push((f.objetivos || '').trim());
    linhas.push('');
    linhas.push('informacoes_gerais:');
    linhas.push((f.informacoesGerais || '').trim());
    return linhas.join('\n');
  };

  /**
   * Renderiza a sidebar com lista de agentes
   */
  const renderSidebar = () => {
    const agentesFiltrados = agents.filter(ag =>
      (ag.nome || '').toLowerCase().includes(busca.toLowerCase())
    );

    return (
      <aside className="agentes-sidebar" style={{ width: typeof sidebarWidth === 'number' ? sidebarWidth : undefined }}>
        <div className="agentes-sidebar-header">
          <h3>Agentes</h3>
          <button
            type="button"
            className="new-agent-button"
            onClick={abrirModalCriacao}
            title="Criar novo agente"
          >
            <span className="plus-icon">+</span>
          </button>
        </div>

        <div className="search-bar">
          <input
            type="text"
            placeholder="Buscar agente..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </div>

        <div className="agentes-lista">
          {loadingAgents && (
            <LoadingSpinner message="Carregando agentes..." />
          )}

          {errorAgents && (
            <div className="nenhum-agente">
              <p style={{ color: '#ff8080' }}>{errorAgents}</p>
            </div>
          )}

          {!loadingAgents && !errorAgents && agentesFiltrados.length === 0 && (
            <div className="nenhum-agente">
              <p>Nenhum agente encontrado</p>
            </div>
          )}

          {!loadingAgents && !errorAgents && agentesFiltrados.map((ag) => (
            <div
              key={ag.id}
              className={`agente-item ${agenteSelecionado?.id === ag.id ? 'selecionado' : ''}`}
              onClick={() => selecionarAgente(ag)}
            >
              <div className="agente-avatar">
                {getIniciais(ag.nome)}
              </div>
              <div className="agente-info">
                <div className="agente-nome">{ag.nome}</div>
                <div className="agente-desc">{getAgentModelLabel(ag)}</div>
              </div>
            </div>
          ))}
        </div>
      </aside>
    );
  };

  /**
   * Renderiza a área principal (editor de agente ou mensagem vazia)
   */
  const renderMainArea = () => {
    if (!agenteSelecionado) {
      return (
        <div
          className="nenhum-agente"
          style={{
            height: '100%',
            backgroundColor: 'var(--background-color)',
            backgroundImage:
              'linear-gradient(to bottom right, rgba(10,20,40,0.9), rgba(10,30,60,0.8)), ' +
              'linear-gradient(rgba(49,117,177,0.1) 1px, transparent 1px), ' +
              'linear-gradient(90deg, rgba(49,117,177,0.1) 1px, transparent 1px)',
            backgroundSize: 'auto, 30px 30px, 30px 30px',
            backgroundRepeat: 'no-repeat, repeat, repeat',
            borderRadius: 8,
          }}
        >
          <div className="nenhum-agente-icone" style={{ marginBottom: 12 }}>
            <picture>
              <source srcSet="/assets/images/5.webp" type="image/webp" />
              <img
                src="/assets/images/5.png"
                alt="Imagem de abertura"
                style={{
                  maxWidth: 390,
                  width: '90%',
                  height: 'auto',
                  objectFit: 'contain',
                  opacity: 0.95,
                  borderRadius: 10,
                  filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.4))',
                  display: 'block',
                  margin: '0 auto',
                }}
              />
            </picture>
          </div>
          <p>Escolha um agente para visualizar e editar.</p>
        </div>
      );
    }

    const isHalo = isHaloAgent(agenteSelecionado);

    return (
      <div className="agente-conteudo">
        <div className="agente-main-header">
          <div className="agente-main-identidade" style={{ alignItems: 'center', gap: 16 }}>
            <div
              className="agente-avatar"
              style={{
                width: 64,
                height: 64,
                fontSize: 22,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
              }}
            >
              {isHalo ? (
                <img
                  src="/assets/images/agentes/halo tools 1.0 logo.webp"
                  alt="Halo Tools 1.0"
                  style={{ width: '110%', height: '110%', objectFit: 'contain', transform: 'translateY(0px)' }}
                />
              ) : (
                getIniciais(agenteSelecionado.nome)
              )}
            </div>
            <div>
              <h4 className="agente-nome" style={{ fontSize: '1.3rem', margin: 0 }}>{agenteSelecionado.nome}</h4>
              <div style={{ marginTop: 4 }}>
                <span className="tag-pequena" style={{ opacity: 0.9 }}>{getAgentModelLabel(agenteSelecionado)}</span>
              </div>
              <div className="agente-canal" style={{ marginTop: 4 }}>Canal: {formData.canal}</div>
            </div>
          </div>
        </div>

        {renderTabs()}
        {renderTabContent()}

        {saveError && (
          <div className="info-box" style={{ borderColor: 'rgba(255,128,128,0.5)', marginTop: 12 }}>
            <p style={{ color: '#ff8080' }}>{saveError}</p>
          </div>
        )}
        {saveSuccess && (
          <div className="info-box" style={{ borderColor: 'rgba(78,84,200,0.5)', marginTop: 12 }}>
            <p style={{ color: '#a8c0d9' }}>{saveSuccess}</p>
          </div>
        )}
      </div>
    );
  };

  /**
   * Salva alterações de perfil e configurações
   */
  const salvarPerfil = async () => {
    try {
      setIsSaving(true);
      setSaveError('');
      setSaveSuccess('');

      const empresaId = getCompanyId();
      if (!empresaId || !agenteSelecionado?.id) {
        throw new Error('Empresa ou agente não identificado');
      }

      // 1. Atualizar tabela agentes
      const { error: agenteError } = await supabase
        .from('agentes')
        .update({
          nome: formData.nome,
          funcao_principal: formData.funcao_principal,
          canal: formData.canal,
          chatbot_enabled: formData.agenteEnabled ?? true
        })
        .eq('id', agenteSelecionado.id);

      if (agenteError) throw new Error(`Erro ao atualizar agente: ${agenteError.message}`);

      // 2. Atualizar tabela halo_nano
      const { error: nanoError } = await supabase
        .from('halo_nano')
        .update({
          temperatura: formData.temperatura,
          habilitar_agente: formData.agenteEnabled ?? true
        })
        .eq('agente_id', agenteSelecionado.id);

      if (nanoError) throw new Error(`Erro ao atualizar halo_nano: ${nanoError.message}`);

      setSaveSuccess('✅ Perfil e configurações salvos com sucesso!');
      
      // Recarregar dados do agente
      setTimeout(() => {
        selecionarAgente(agenteSelecionado);
      }, 1000);

    } catch (err) {
      console.error('[AgentsPage] Erro ao salvar perfil:', err);
      setSaveError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Salva alterações de infraestrutura (conexão e projeto)
   */
  const salvarInfraestrutura = async () => {
    try {
      setIsSaving(true);
      setSaveError('');
      setSaveSuccess('');

      const empresaId = getCompanyId();
      if (!empresaId || !agenteSelecionado?.id) {
        throw new Error('Empresa ou agente não identificado');
      }

      // 1. Atualizar conexão no agente
      const { data: agenteData, error: agenteError } = await supabase
        .from('agentes')
        .update({ conexao_id: formData.conexao_id || null })
        .eq('id', agenteSelecionado.id)
        .eq('empresa_id', empresaId)
        .select();

      if (agenteError) throw new Error(`Erro ao atualizar agente: ${agenteError.message}`);

      // 2. Atualizar conexão na tabela halo_nano
      const { data: nanoData, error: nanoError } = await supabase
        .from('halo_nano')
        .update({ conexao_id: formData.conexao_id || null })
        .eq('agente_id', agenteSelecionado.id)
        .eq('empresa_id', empresaId)
        .select();

      if (nanoError) throw new Error(`Erro ao atualizar halo_nano: ${nanoError.message}`);

      // 3. Desvincular agente de conexões anteriores
      const { error: desvinculaConnError } = await supabase
        .from('connections')
        .update({ agente_id: null, with_ai: false })
        .eq('agente_id', agenteSelecionado.id)
        .eq('company_id', empresaId);

      // 4. Vincular nova conexão (se selecionada)
      if (formData.conexao_id) {
        const { error: connError } = await supabase
          .from('connections')
          .update({ 
            agente_id: agenteSelecionado.id,
            with_ai: true 
          })
          .eq('id', formData.conexao_id)
          .eq('company_id', empresaId);

        if (connError) throw new Error(`Erro ao vincular conexão: ${connError.message}`);
      }

      // 5. Desvincular projeto anterior
      const { error: desvinculaProjError } = await supabase
        .from('projects')
        .update({ ai_agent_id: null })
        .eq('ai_agent_id', agenteSelecionado.id)
        .eq('company_id', empresaId);

      // 6. Vincular novo projeto (se selecionado)
      if (formData.projeto_id) {
        const { error: projError } = await supabase
          .from('projects')
          .update({ ai_agent_id: agenteSelecionado.id })
          .eq('id', formData.projeto_id)
          .eq('company_id', empresaId);

        if (projError) throw new Error(`Erro ao vincular projeto: ${projError.message}`);
      }

      setSaveSuccess('✅ Infraestrutura salva com sucesso!');
      
      // Recarregar dados do agente após 500ms
      setTimeout(() => {
        // Forçar recarregamento dos dados
        const agenteAtualizado = { ...agenteSelecionado };
        setAgenteSelecionado(agenteAtualizado);
      }, 500);

    } catch (err) {
      console.error('[AgentsPage] Erro ao salvar infraestrutura:', err);
      setSaveError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  // ========== RENDERIZAÇÃO DAS 10 ETAPAS ==========
  
  const renderEtapa1Empresa = () => {
    return (
      <div className="tab-content">
        <h3>📊 Dados da Empresa</h3>
        <div style={{ marginTop: '1.5rem' }}>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Nome Fantasia</label>
            <input type="text" value={formData.empresa?.nome_fantasia || ''} disabled style={{ width: '100%', padding: '0.75rem', background: '#1a1a2e', border: '1px solid #2a2a3e', borderRadius: '8px', color: '#fff' }} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Segmento</label>
              <input type="text" value={formData.empresa?.segmento || ''} disabled style={{ width: '100%', padding: '0.75rem', background: '#1a1a2e', border: '1px solid #2a2a3e', borderRadius: '8px', color: '#fff' }} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Subsegmento</label>
              <input type="text" value={formData.empresa?.subsegmento || ''} disabled style={{ width: '100%', padding: '0.75rem', background: '#1a1a2e', border: '1px solid #2a2a3e', borderRadius: '8px', color: '#fff' }} />
            </div>
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Cidade / Estado</label>
            <input type="text" value={`${formData.empresa?.cidade || ''}, ${formData.empresa?.estado || ''}`} disabled style={{ width: '100%', padding: '0.75rem', background: '#1a1a2e', border: '1px solid #2a2a3e', borderRadius: '8px', color: '#fff' }} />
          </div>
        </div>
      </div>
    );
  };

  const renderEtapa2Infraestrutura = () => {
    return (
      <div className="tab-content">
        <h3>🔌 Infraestrutura</h3>
        <div style={{ marginTop: '1.5rem' }}>
          {/* CONEXÃO WHATSAPP */}
          <div style={{ marginBottom: '2rem' }}>
            <h4 style={{ marginBottom: '1rem' }}>📱 Conexão WhatsApp</h4>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Selecionar Conexão</label>
              <select 
                value={formData.conexao_id || ''} 
                onChange={(e) => {
                  const conexaoId = e.target.value || null;
                  setFormData(prev => ({ ...prev, conexao_id: conexaoId }));
                }}
                style={{ width: '100%', padding: '0.75rem', background: '#1a1a2e', border: '1px solid #2a2a3e', borderRadius: '8px', color: '#fff' }}
              >
                <option value="">Nenhuma conexão</option>
                {whatsappConnections.map(conn => (
                  <option key={conn.id} value={conn.id}>
                    {conn.name} - {conn.number}
                  </option>
                ))}
              </select>
            </div>
            {formData.conexao_nome && (
              <div style={{ padding: '1rem', background: 'rgba(139, 92, 246, 0.1)', border: '1px solid rgba(139, 92, 246, 0.3)', borderRadius: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontSize: '1.5rem' }}>✅</span>
                  <div>
                    <p style={{ margin: 0, fontWeight: 600 }}>{formData.conexao_nome}</p>
                    <p style={{ margin: 0, opacity: 0.7, fontSize: '0.9rem' }}>{formData.conexao_numero}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* PROJETO CRM */}
          <div style={{ marginBottom: '2rem' }}>
            <h4 style={{ marginBottom: '1rem' }}>📋 Projeto CRM</h4>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Selecionar Projeto</label>
              <select 
                value={formData.projeto_id || ''} 
                onChange={(e) => {
                  const projetoId = e.target.value || null;
                  setFormData(prev => ({ ...prev, projeto_id: projetoId }));
                }}
                style={{ width: '100%', padding: '0.75rem', background: '#1a1a2e', border: '1px solid #2a2a3e', borderRadius: '8px', color: '#fff' }}
              >
                <option value="">Nenhum projeto</option>
                {projetosDisponiveis.map(proj => (
                  <option key={proj.id} value={proj.id}>
                    {proj.name}
                  </option>
                ))}
              </select>
            </div>
            {formData.projeto_nome && (
              <div style={{ padding: '1rem', background: 'rgba(139, 92, 246, 0.1)', border: '1px solid rgba(139, 92, 246, 0.3)', borderRadius: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontSize: '1.5rem' }}>✅</span>
                  <p style={{ margin: 0, fontWeight: 600 }}>{formData.projeto_nome}</p>
                </div>
              </div>
            )}
          </div>

          {/* BOTÃO SALVAR */}
          <button
            onClick={salvarInfraestrutura}
            disabled={isSaving}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'linear-gradient(135deg, #b43bea 0%, #7c3aed 100%)',
              border: 'none',
              borderRadius: '8px',
              color: '#fff',
              fontWeight: 600,
              cursor: isSaving ? 'not-allowed' : 'pointer',
              opacity: isSaving ? 0.6 : 1,
              width: '100%',
              fontSize: '1rem'
            }}
          >
            {isSaving ? '💾 Salvando...' : '💾 Salvar Infraestrutura'}
          </button>

          {/* MENSAGENS */}
          {saveError && (
            <div style={{ marginTop: '1rem', padding: '1rem', background: 'rgba(255, 128, 128, 0.1)', border: '1px solid rgba(255, 128, 128, 0.3)', borderRadius: '8px' }}>
              <p style={{ margin: 0, color: '#ff8080' }}>{saveError}</p>
            </div>
          )}
          {saveSuccess && (
            <div style={{ marginTop: '1rem', padding: '1rem', background: 'rgba(139, 92, 246, 0.1)', border: '1px solid rgba(139, 92, 246, 0.3)', borderRadius: '8px' }}>
              <p style={{ margin: 0, color: '#8b5cf6' }}>{saveSuccess}</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderEtapa3Perfil = () => {
    const temp = formData.temperatura || 0.7;
    return (
      <div className="tab-content">
        <h3>👤 Perfil & Configurações</h3>
        
        {/* PERFIL */}
        <div style={{ marginTop: '1.5rem', marginBottom: '2rem' }}>
          <h4 style={{ marginBottom: '1rem', color: '#8b5cf6' }}>Informações do Agente</h4>
          
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Nome do Agente</label>
            <input 
              type="text" 
              value={formData.nome || ''} 
              onChange={(e) => setFormData(prev => ({ ...prev, nome: e.target.value }))}
              style={{ width: '100%', padding: '0.75rem', background: '#1a1a2e', border: '1px solid #2a2a3e', borderRadius: '8px', color: '#fff' }}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Função Principal</label>
            <input 
              type="text" 
              value={formData.funcao_principal || ''} 
              onChange={(e) => setFormData(prev => ({ ...prev, funcao_principal: e.target.value }))}
              style={{ width: '100%', padding: '0.75rem', background: '#1a1a2e', border: '1px solid #2a2a3e', borderRadius: '8px', color: '#fff' }}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Canal</label>
            <select 
              value={formData.canal || 'WhatsApp'} 
              onChange={(e) => setFormData(prev => ({ ...prev, canal: e.target.value }))}
              style={{ width: '100%', padding: '0.75rem', background: '#1a1a2e', border: '1px solid #2a2a3e', borderRadius: '8px', color: '#fff' }}
            >
              <option value="WhatsApp">WhatsApp</option>
              <option value="Interno">Interno</option>
            </select>
          </div>
        </div>

        {/* TEMPERATURA */}
        <div style={{ marginBottom: '2rem' }}>
          <h4 style={{ marginBottom: '1rem', color: '#8b5cf6' }}>🌡️ Temperatura (Criatividade)</h4>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
              Temperatura: {temp.toFixed(1)}
            </label>
            <input 
              type="range" 
              min="0" 
              max="1" 
              step="0.1"
              value={temp}
              onChange={(e) => setFormData(prev => ({ ...prev, temperatura: parseFloat(e.target.value) }))}
              style={{ width: '100%' }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', opacity: 0.7, marginTop: '0.25rem' }}>
              <span>Preciso (0.0)</span>
              <span>Criativo (1.0)</span>
            </div>
          </div>
        </div>

        {/* CONFIGURAÇÕES */}
        <div style={{ marginBottom: '2rem' }}>
          <h4 style={{ marginBottom: '1rem', color: '#8b5cf6' }}>⚙️ Configurações</h4>
          
          {/* Toggle Habilitar Agente */}
          <div style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <label style={{ fontWeight: 500 }}>Habilitar Agente</label>
            <div 
              onClick={() => setFormData(prev => ({ ...prev, agenteEnabled: !prev.agenteEnabled }))}
              style={{
                width: '50px',
                height: '26px',
                background: formData.agenteEnabled ? 'linear-gradient(135deg, var(--primary-color), var(--secondary-color))' : '#2a2a3e',
                borderRadius: '13px',
                position: 'relative',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                border: '1px solid ' + (formData.agenteEnabled ? 'var(--primary-color)' : '#3a3a4e')
              }}
            >
              <div style={{
                width: '20px',
                height: '20px',
                background: '#fff',
                borderRadius: '50%',
                position: 'absolute',
                top: '2px',
                left: formData.agenteEnabled ? '26px' : '2px',
                transition: 'all 0.3s ease',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }} />
            </div>
          </div>
        </div>

        {/* BOTÃO SALVAR */}
        <button
          onClick={salvarPerfil}
          disabled={isSaving}
          className="new-agent-button"
          style={{
            width: '100%',
            height: 'auto',
            padding: '0.75rem 1.5rem',
            fontSize: '1rem',
            minWidth: 'auto'
          }}
        >
          {isSaving ? '💾 Salvando...' : '💾 Salvar Perfil & Configurações'}
        </button>

        {/* MENSAGENS */}
        {saveError && (
          <div style={{ marginTop: '1rem', padding: '1rem', background: 'rgba(255, 128, 128, 0.1)', border: '1px solid rgba(255, 128, 128, 0.3)', borderRadius: '8px' }}>
            <p style={{ margin: 0, color: '#ff8080' }}>{saveError}</p>
          </div>
        )}
        {saveSuccess && (
          <div style={{ marginTop: '1rem', padding: '1rem', background: 'rgba(139, 92, 246, 0.1)', border: '1px solid rgba(139, 92, 246, 0.3)', borderRadius: '8px' }}>
            <p style={{ margin: 0, color: '#8b5cf6' }}>{saveSuccess}</p>
          </div>
        )}
      </div>
    );
  };

  const renderEtapa4Configuracoes = () => {
    return renderHaloConfig();
  };

  const renderEtapa5Temperatura = () => {
    const temp = formData.temperatura || 7;
    return (
      <div className="tab-content">
        <h3>🌡️ Temperatura da IA</h3>
        <div style={{ marginTop: '1.5rem' }}>
          <p style={{ marginBottom: '1rem', opacity: 0.8 }}>Controla a criatividade das respostas (0 = preciso, 10 = criativo)</p>
          <div style={{ marginBottom: '1rem' }}>
            <input 
              type="range" 
              min="0" 
              max="10" 
              value={temp} 
              onChange={(e) => setFormData(prev => ({ ...prev, temperatura: parseInt(e.target.value) }))}
              style={{ width: '100%' }}
            />
            <div style={{ textAlign: 'center', fontSize: '2rem', fontWeight: 'bold', marginTop: '1rem', color: '#8b5cf6' }}>
              {temp}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderEtapa6BaseConhecimento = () => {
    return (
      <div className="tab-content">
        <h3>📚 Base de Conhecimento</h3>
        
        <div style={{ marginTop: '1.5rem' }}>
          <p style={{ opacity: 0.7, marginBottom: '1rem' }}>Documentos e URLs adicionados à base de conhecimento do agente.</p>
          
          {/* Botão Adicionar */}
          <button
            onClick={() => setShowAddModal(true)}
            className="new-agent-button"
            style={{ marginBottom: '1.5rem' }}
          >
            <AppIcon name="add" style={{ fontSize: '1.2rem' }} />
            Adicionar Documento
          </button>

          {/* Lista de Documentos */}
          {loadingKnowledge ? (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              <AppIcon name="sync" className="spinning" style={{ fontSize: '2rem', color: '#8b5cf6' }} />
              <p style={{ marginTop: '0.5rem', opacity: 0.7 }}>Carregando...</p>
            </div>
          ) : knowledgeDocs.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {knowledgeDocs.map((doc) => (
                <div key={doc.id} style={{ padding: '1rem', background: '#1a1a2e', border: '1px solid #2a2a3e', borderRadius: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <AppIcon name={doc.source_type === 'url' ? 'link' : 'description'} style={{ fontSize: '1.2rem', color: '#8b5cf6' }} />
                        <strong>{doc.source_name}</strong>
                      </div>
                      {doc.source_url && (
                        <p style={{ margin: '0.25rem 0', opacity: 0.7, fontSize: '0.85rem', wordBreak: 'break-all' }}>
                          {doc.source_url}
                        </p>
                      )}
                      <p style={{ margin: '0.5rem 0 0 0', opacity: 0.5, fontSize: '0.8rem' }}>
                        Adicionado em {new Date(doc.created_at).toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                    <button
                      onClick={() => deletarDocumento(doc.id)}
                      style={{
                        padding: '0.5rem',
                        background: 'rgba(255, 80, 80, 0.1)',
                        border: '1px solid rgba(255, 80, 80, 0.3)',
                        borderRadius: '6px',
                        color: '#ff5050',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <AppIcon name="delete" style={{ fontSize: '1.2rem' }} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ padding: '2rem', background: 'rgba(139, 92, 246, 0.1)', border: '1px dashed rgba(139, 92, 246, 0.3)', borderRadius: '8px', textAlign: 'center' }}>
              <p style={{ margin: 0, opacity: 0.6 }}>Nenhum documento adicionado ainda</p>
            </div>
          )}
        </div>

        {/* Modal Adicionar */}
        {showAddModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}>
            <div style={{
              background: '#1a1a2e',
              padding: '2rem',
              borderRadius: '12px',
              maxWidth: '500px',
              width: '90%',
              border: '1px solid #2a2a3e'
            }}>
              <h3 style={{ marginTop: 0 }}>Adicionar à Base de Conhecimento</h3>
              
              {/* Tabs */}
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
                <button
                  onClick={() => setAddType('url')}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    background: addType === 'url' ? 'linear-gradient(135deg, var(--primary-color), var(--secondary-color))' : '#2a2a3e',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                    cursor: 'pointer'
                  }}
                >
                  🔗 URL
                </button>
                <button
                  onClick={() => setAddType('file')}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    background: addType === 'file' ? 'linear-gradient(135deg, var(--primary-color), var(--secondary-color))' : '#2a2a3e',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                    cursor: 'pointer'
                  }}
                >
                  📄 Arquivo
                </button>
              </div>

              {/* Conteúdo */}
              {addType === 'url' ? (
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem' }}>URL do Site</label>
                  <input
                    type="url"
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                    placeholder="https://exemplo.com"
                    style={{ width: '100%', padding: '0.75rem', background: '#0a0a1e', border: '1px solid #2a2a3e', borderRadius: '8px', color: '#fff', marginBottom: '1rem' }}
                  />
                </div>
              ) : (
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem' }}>Arquivo (PDF, TXT, DOCX)</label>
                  <input
                    type="file"
                    onChange={(e) => setFileInput(e.target.files[0])}
                    accept=".pdf,.txt,.doc,.docx"
                    style={{ width: '100%', padding: '0.75rem', background: '#0a0a1e', border: '1px solid #2a2a3e', borderRadius: '8px', color: '#fff', marginBottom: '1rem' }}
                  />
                </div>
              )}

              {addError && (
                <div style={{ padding: '0.75rem', background: 'rgba(255, 80, 80, 0.1)', border: '1px solid rgba(255, 80, 80, 0.3)', borderRadius: '8px', marginBottom: '1rem' }}>
                  <p style={{ margin: 0, color: '#ff5050', fontSize: '0.9rem' }}>{addError}</p>
                </div>
              )}

              {/* Botões */}
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={() => setShowAddModal(false)}
                  disabled={addingKnowledge}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    background: '#2a2a3e',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                    cursor: 'pointer'
                  }}
                >
                  Cancelar
                </button>
                <button
                  onClick={addType === 'url' ? adicionarURL : adicionarArquivo}
                  disabled={addingKnowledge || (addType === 'url' ? !urlInput : !fileInput)}
                  className="new-agent-button"
                  style={{ flex: 1 }}
                >
                  {addingKnowledge ? 'Adicionando...' : 'Adicionar'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderEtapa7Catalogos = () => {
    return (
      <div className="tab-content">
        <h3>🛠️ Ferramentas do Agente</h3>
        
        {/* CATÁLOGOS */}
        <div style={{ marginTop: '1.5rem', marginBottom: '2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h4 style={{ margin: 0, color: '#8b5cf6' }}>📦 Catálogos de Produtos</h4>
            {!editandoCatalogos ? (
              <button
                onClick={() => setEditandoCatalogos(true)}
                className="new-agent-button"
                style={{ padding: '0.5rem 1rem', fontSize: '0.9rem', minWidth: 'auto', height: 'auto' }}
              >
                <AppIcon name="edit" style={{ fontSize: '1rem' }} />
                Editar
              </button>
            ) : (
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={() => {
                    setCatalogosTemp([...categoriasSelecionadas]);
                    setEditandoCatalogos(false);
                  }}
                  style={{ padding: '0.5rem 1rem', background: '#2a2a3e', border: 'none', borderRadius: '6px', color: '#fff', cursor: 'pointer' }}
                >
                  Cancelar
                </button>
                <button
                  onClick={salvarCatalogos}
                  disabled={isSaving}
                  className="new-agent-button"
                  style={{ padding: '0.5rem 1rem', fontSize: '0.9rem', minWidth: 'auto', height: 'auto' }}
                >
                  {isSaving ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            )}
          </div>

          {editandoCatalogos ? (
            // Modo edição - checkboxes
            loadingCategorias ? (
              <div style={{ padding: '2rem', textAlign: 'center' }}>
                <AppIcon name="sync" className="spinning" style={{ fontSize: '2rem', color: '#8b5cf6' }} />
                <p style={{ marginTop: '0.5rem', opacity: 0.7 }}>Carregando...</p>
              </div>
            ) : categoriasDisponiveis.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {categoriasDisponiveis.map((cat) => {
                  const isSelected = catalogosTemp.includes(cat.id);
                  return (
                    <div
                      key={cat.id}
                      onClick={() => {
                        if (isSelected) {
                          setCatalogosTemp(prev => prev.filter(id => id !== cat.id));
                        } else {
                          setCatalogosTemp(prev => [...prev, cat.id]);
                        }
                      }}
                      style={{
                        padding: '1rem',
                        background: isSelected ? 'rgba(139, 92, 246, 0.1)' : '#1a1a2e',
                        border: `2px solid ${isSelected ? '#8b5cf6' : '#2a2a3e'}`,
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <strong>{cat.nome}</strong>
                          <p style={{ margin: '0.25rem 0 0 0', opacity: 0.7, fontSize: '0.9rem' }}>
                            {cat.total_produtos} {cat.total_produtos === 1 ? 'produto' : 'produtos'}
                          </p>
                        </div>
                        {isSelected && <AppIcon name="check_circle" style={{ color: '#8b5cf6' }} />}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ padding: '2rem', background: 'rgba(139, 92, 246, 0.1)', border: '1px dashed rgba(139, 92, 246, 0.3)', borderRadius: '8px', textAlign: 'center' }}>
                <p style={{ margin: 0, opacity: 0.6 }}>Nenhuma categoria disponível</p>
              </div>
            )
          ) : (
            // Modo visualização
            categoriasSelecionadas && categoriasSelecionadas.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {categoriasDisponiveis.filter(cat => categoriasSelecionadas.includes(cat.id)).map((cat) => (
                  <div key={cat.id} style={{ padding: '1rem', background: '#1a1a2e', border: '1px solid #2a2a3e', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <strong>{cat.nome}</strong>
                        <p style={{ margin: '0.25rem 0 0 0', opacity: 0.7, fontSize: '0.9rem' }}>
                          {cat.total_produtos} {cat.total_produtos === 1 ? 'produto' : 'produtos'}
                        </p>
                      </div>
                      <span style={{ color: '#8b5cf6' }}>✓</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ padding: '2rem', background: 'rgba(139, 92, 246, 0.1)', border: '1px dashed rgba(139, 92, 246, 0.3)', borderRadius: '8px', textAlign: 'center' }}>
                <p style={{ margin: 0, opacity: 0.6 }}>Nenhum catálogo selecionado</p>
              </div>
            )
          )}
        </div>

        {/* ✅ NOVO: Toggle para Carousel do WhatsApp (Modo Visualização) */}
        {!editandoCatalogos && categoriasSelecionadas.length > 0 && (
          <div style={{ marginTop: '1.5rem', padding: '1rem', background: 'rgba(68, 225, 255, 0.1)', border: '1px solid rgba(68, 225, 255, 0.3)', borderRadius: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <AppIcon name="shopping_cart" style={{ color: '#44e1ff', fontSize: '1.3rem' }} />
                <label style={{ color: '#ffffff', fontWeight: 500, margin: 0, cursor: 'pointer' }}>
                  📱 Enviar via Carousel do WhatsApp
                </label>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={enableWhatsappCatalog}
                onClick={() => setEnableWhatsappCatalog(!enableWhatsappCatalog)}
                style={{
                  width: '48px',
                  height: '26px',
                  borderRadius: '13px',
                  border: '1px solid ' + (enableWhatsappCatalog ? 'rgba(68, 225, 255, 0.5)' : 'rgba(255,255,255,0.2)'),
                  background: enableWhatsappCatalog ? 'linear-gradient(135deg, #44e1ff, #3b82f6)' : 'rgba(0,0,0,0.25)',
                  position: 'relative',
                  cursor: 'pointer',
                  padding: 0,
                  transition: 'all 0.3s ease'
                }}
              >
                <span
                  style={{
                    position: 'absolute',
                    top: '2px',
                    left: enableWhatsappCatalog ? '24px' : '2px',
                    width: '22px',
                    height: '22px',
                    borderRadius: '50%',
                    background: '#fff',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                  }}
                />
              </button>
            </div>
            
            {enableWhatsappCatalog && (
              <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: 'rgba(68, 225, 255, 0.05)', borderRadius: 6, borderLeft: '3px solid #44e1ff' }}>
                <p style={{ margin: '0 0 0.5rem 0', color: '#44e1ff', fontSize: '0.9rem', fontWeight: 500 }}>✨ Carousel habilitado!</p>
                <ul style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.2rem', color: '#a8c0d9', fontSize: '0.85rem', lineHeight: '1.5' }}>
                  <li>Produtos serão enviados em formato visual e interativo</li>
                  <li>Cliente pode deslizar e clicar em "Ver Detalhes"</li>
                  <li>Sua mensagem aparecerá acima do Carousel</li>
                  <li>Todos os produtos da categoria serão inclusos</li>
                </ul>
              </div>
            )}
            
            {/* ✅ Botão de salvar rápido */}
            <button
              onClick={salvarFerramentas}
              disabled={saving}
              style={{
                marginTop: '1rem',
                padding: '0.75rem 1.5rem',
                background: 'linear-gradient(135deg, #44e1ff, #3b82f6)',
                border: 'none',
                borderRadius: '8px',
                color: '#fff',
                fontWeight: 600,
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.6 : 1,
                width: '100%',
                fontSize: '0.95rem',
                transition: 'all 0.3s ease'
              }}
            >
              {saving ? '💾 Salvando...' : '💾 Salvar Carousel'}
            </button>
            
            {/* Mensagens de feedback */}
            {saveError && (
              <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: 'rgba(255, 128, 128, 0.1)', border: '1px solid rgba(255, 128, 128, 0.3)', borderRadius: 6, color: '#ff8080', fontSize: '0.9rem' }}>
                {saveError}
              </div>
            )}
            {saveSuccess && (
              <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: 'rgba(68, 225, 255, 0.1)', border: '1px solid rgba(68, 225, 255, 0.3)', borderRadius: 6, color: '#44e1ff', fontSize: '0.9rem' }}>
                {saveSuccess}
              </div>
            )}
          </div>
        )}

        {/* FORMULÁRIO - Centralizado no Projeto CRM */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h4 style={{ margin: 0, color: '#8b5cf6' }}>📝 Formulário de Coleta</h4>
          </div>
          
          <div style={{ padding: '1.5rem', background: 'rgba(68, 225, 255, 0.1)', border: '1px solid rgba(68, 225, 255, 0.3)', borderRadius: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
              <AppIcon name="info" style={{ color: '#44e1ff', fontSize: '1.5rem' }} />
              <div>
                <p style={{ margin: '0 0 0.5rem 0', fontWeight: 600, color: '#44e1ff' }}>Formulário centralizado no Projeto CRM</p>
                <p style={{ margin: 0, opacity: 0.9, fontSize: '0.9rem', lineHeight: '1.6' }}>
                  Os campos do formulário são configurados diretamente na lista <strong>"Equipe de Vendas"</strong> do Projeto CRM vinculado a este agente.
                  {formData.projeto_nome && (
                    <span> Projeto atual: <strong>{formData.projeto_nome}</strong></span>
                  )}
                </p>
                <p style={{ margin: '0.5rem 0 0 0', opacity: 0.6, fontSize: '0.85rem' }}>
                  Acesse: Projetos &gt; {formData.projeto_nome || 'Seu Projeto CRM'} &gt; Lista "Equipe de Vendas" &gt; Configurações
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* MENSAGENS */}
        {saveError && (
          <div style={{ marginTop: '1rem', padding: '1rem', background: 'rgba(255, 128, 128, 0.1)', border: '1px solid rgba(255, 128, 128, 0.3)', borderRadius: '8px' }}>
            <p style={{ margin: 0, color: '#ff8080' }}>{saveError}</p>
          </div>
        )}
        {saveSuccess && (
          <div style={{ marginTop: '1rem', padding: '1rem', background: 'rgba(139, 92, 246, 0.1)', border: '1px solid rgba(139, 92, 246, 0.3)', borderRadius: '8px' }}>
            <p style={{ margin: 0, color: '#8b5cf6' }}>{saveSuccess}</p>
          </div>
        )}
      </div>
    );
  };

  const renderEtapa8Formulario = () => {
    return renderEtapa7Catalogos(); // Redirecionado para etapa 7
  };

  const renderEtapa9Resumo = () => {
    return (
      <div className="tab-content">
        <h3>📋 Resumo da Configuração</h3>
        <div style={{ marginTop: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div style={{ padding: '1rem', background: '#1a1a2e', border: '1px solid #2a2a3e', borderRadius: '8px' }}>
            <h4 style={{ margin: '0 0 0.5rem 0' }}>👤 Agente</h4>
            <p style={{ margin: 0 }}><strong>Nome:</strong> {formData.nome}</p>
            <p style={{ margin: 0 }}><strong>Função:</strong> {formData.funcao_principal}</p>
          </div>
          <div style={{ padding: '1rem', background: '#1a1a2e', border: '1px solid #2a2a3e', borderRadius: '8px' }}>
            <h4 style={{ margin: '0 0 0.5rem 0' }}>🔧 Ferramentas</h4>
            <p style={{ margin: 0 }}><strong>Catálogos:</strong> {formData.catalogos_ids?.length || 0}</p>
            <p style={{ margin: 0 }}><strong>Campos Formulário:</strong> {formData.campos_formulario?.length || 0}</p>
          </div>
          <div style={{ padding: '1rem', background: '#1a1a2e', border: '1px solid #2a2a3e', borderRadius: '8px' }}>
            <h4 style={{ margin: '0 0 0.5rem 0' }}>⚙️ Configurações</h4>
            <p style={{ margin: 0 }}><strong>Temperatura:</strong> {formData.temperatura || 7}</p>
            <p style={{ margin: 0 }}><strong>Conexão:</strong> {formData.conexao_id ? '✅ Vinculada' : '❌ Não vinculada'}</p>
          </div>
        </div>
      </div>
    );
  };

  const renderEtapa10Prompt = () => {
    return renderHaloInstrucoes();
  };

  /**
   * Renderiza as abas de navegação
   */
  const renderTabs = () => {
    const isHalo = isHaloAgent(agenteSelecionado);

    return (
      <div className="settings-tabs">
        {isHalo ? (
          <>
            <button type="button" className={`tab-button ${abaAtiva === 'etapa1' ? 'active' : ''}`} onClick={() => setAbaAtiva('etapa1')}>
              📊 Empresa
            </button>
            <button type="button" className={`tab-button ${abaAtiva === 'etapa2' ? 'active' : ''}`} onClick={() => setAbaAtiva('etapa2')}>
              🔌 Infraestrutura
            </button>
            <button type="button" className={`tab-button ${abaAtiva === 'etapa3' ? 'active' : ''}`} onClick={() => setAbaAtiva('etapa3')}>
              👤 Perfil & Config
            </button>
            <button type="button" className={`tab-button ${abaAtiva === 'etapa6' ? 'active' : ''}`} onClick={() => setAbaAtiva('etapa6')}>
              📚 Base Conhecimento
            </button>
            <button type="button" className={`tab-button ${abaAtiva === 'etapa7' ? 'active' : ''}`} onClick={() => setAbaAtiva('etapa7')}>
              🛠️ Ferramentas
            </button>
            <button type="button" className={`tab-button ${abaAtiva === 'etapa10' ? 'active' : ''}`} onClick={() => setAbaAtiva('etapa10')}>
              💬 Prompt
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className={`tab-button ${abaAtiva === 'perfil' ? 'active' : ''}`}
              onClick={() => setAbaAtiva('perfil')}
            >
              Perfil
            </button>
            <button
              type="button"
              className={`tab-button ${abaAtiva === 'instrucoes' ? 'active' : ''}`}
              onClick={() => setAbaAtiva('instrucoes')}
            >
              Instruções
            </button>
            <button
              type="button"
              className={`tab-button ${abaAtiva === 'knowledge' ? 'active' : ''}`}
              onClick={() => setAbaAtiva('knowledge')}
            >
              Conhecimento
            </button>
            <button
              type="button"
              className={`tab-button ${abaAtiva === 'config' ? 'active' : ''}`}
              onClick={() => setAbaAtiva('config')}
            >
              Configurações
            </button>
          </>
        )}
      </div>
    );
  };

  /**
   * Renderiza o conteúdo da aba ativa
   */
  const renderTabContent = () => {
    const isHalo = isHaloAgent(agenteSelecionado);

    if (isHalo) {
      switch (abaAtiva) {
        case 'etapa1':
          return renderEtapa1Empresa();
        case 'etapa2':
          return renderEtapa2Infraestrutura();
        case 'etapa3':
          return renderEtapa3Perfil();
        case 'etapa6':
          return renderEtapa6BaseConhecimento();
        case 'etapa7':
          return renderEtapa7Catalogos();
        case 'etapa10':
          return renderEtapa10Prompt();
        default:
          return renderEtapa1Empresa();
      }
    }

    switch (abaAtiva) {
      case 'perfil':
        return renderPerfil();
      case 'instrucoes':
        return renderInstrucoes();
      case 'knowledge':
        return renderKnowledge();
      case 'config':
        return renderConfig();
      default:
        return null;
    }
  };

  /**
   * Renderiza aba Perfil (agentes normais)
   */
  const renderPerfil = () => {
    return (
      <div className="tab-content">
        <h3>Perfil do Agente</h3>
        <p style={{ color: '#9bb3c7' }}>
          Conteúdo da aba Perfil será implementado aqui
        </p>
      </div>
    );
  };

  /**
   * Renderiza aba Instruções (agentes normais)
   */
  const renderInstrucoes = () => {
    return (
      <div className="tab-content">
        <h3>Instruções</h3>
        <p style={{ color: '#9bb3c7' }}>
          Conteúdo da aba Instruções será implementado aqui
        </p>
      </div>
    );
  };

  /**
   * Renderiza aba Knowledge (agentes normais)
   */
  const renderKnowledge = () => {
    return (
      <div className="tab-content">
        <h3>Conhecimento</h3>
        <p style={{ color: '#9bb3c7' }}>
          Conteúdo da aba Knowledge será implementado aqui
        </p>
      </div>
    );
  };

  /**
   * Renderiza aba Config (agentes normais)
   */
  const renderConfig = () => {
    return (
      <div className="tab-content">
        <h3>Configurações</h3>
        <p style={{ color: '#9bb3c7' }}>
          Conteúdo da aba Config será implementado aqui
        </p>
      </div>
    );
  };

  /**
   * Renderiza aba Perfil (Halo Tools) - Etapa 1
   */
  const renderHaloPerfil = () => {
    return (
      <div className="tab-content">
        <div style={{ marginBottom: 12, color: '#9bb3c7' }}>Etapa 1 · Configurações iniciais</div>
        
        <div className="form-grid-2">
          <div className="form-field">
            <label>Nome do agente</label>
            <input 
              type="text" 
              value={formData.nome} 
              onChange={e => setFormData(p => ({ ...p, nome: e.target.value }))} 
              placeholder="Ex.: Halo Vendas" 
            />
          </div>
          <div className="form-field">
            <label>Conexão</label>
            <select 
              value={formData.canal} 
              onChange={e => setFormData(p => ({ ...p, canal: e.target.value }))}
            >
              <option value="Interno">Interno</option>
              <option value="WhatsApp">WhatsApp</option>
              <option value="Telegram">Telegram</option>
            </select>
          </div>
        </div>

        {formData.canal === 'Interno' && (
          <div className="hint" style={{ color: '#a8c0d9', marginBottom: 8 }}>
            Nenhuma configuração adicional para conexão interna.
          </div>
        )}
        
        {formData.canal === 'WhatsApp' && (
          <div className="form-field" style={{ marginTop: 8 }}>
            <label>Selecionar conexão WhatsApp</label>
            <select 
              value={formData.whatsappConnectionId || ''} 
              onChange={e => setFormData(p => ({ ...p, whatsappConnectionId: e.target.value }))}
            >
              <option value="">Selecione uma conexão...</option>
              {whatsappConnections.map(conn => {
                const nome = conn.name || 'Sem nome';
                const numero = conn.number ? ` - ${conn.number}` : '';
                return (
                  <option key={conn.id} value={conn.id}>
                    {nome}{numero}
                  </option>
                );
              })}
            </select>
            {whatsappConnections.length === 0 && (
              <small style={{ color: '#fca5a5' }}>Nenhuma conexão WhatsApp disponível. Configure uma conexão primeiro.</small>
            )}
          </div>
        )}
        
        {formData.canal === 'Telegram' && (
          <div className="hint" style={{ color: '#a8c0d9', marginBottom: 8 }}>
            Integração com Telegram: em breve.
          </div>
        )}

        <div className="form-grid-2" style={{ marginTop: 8 }}>
          <div className="form-field">
            <label>Função principal</label>
            <select 
              value={formData.funcaoPrincipal || ''} 
              onChange={e => setFormData(p => ({ ...p, funcaoPrincipal: e.target.value, subFuncao: '' }))}
            >
              <option value="">Selecione...</option>
              <option value="atendimento">Atendimento ao cliente</option>
              <option value="suporte_interno">Suporte interno</option>
            </select>
          </div>
          <div className="form-field">
            <label>Subopções</label>
            {formData.funcaoPrincipal === 'atendimento' && (
              <select 
                value={formData.subFuncao || ''} 
                onChange={e => setFormData(p => ({ ...p, subFuncao: e.target.value }))}
              >
                <option value="">Selecione...</option>
                <option value="vendas">Vendas</option>
                <option value="suporte">Suporte</option>
              </select>
            )}
            {formData.funcaoPrincipal === 'suporte_interno' && (
              <select 
                value={formData.subFuncao || ''} 
                onChange={e => setFormData(p => ({ ...p, subFuncao: e.target.value }))}
              >
                <option value="">Selecione...</option>
                <option value="gestao">Gestão</option>
                <option value="treinamento">Treinamento</option>
                <option value="especialista">Especialista</option>
              </select>
            )}
            {!formData.funcaoPrincipal && (
              <select disabled>
                <option>Escolha uma função principal primeiro</option>
              </select>
            )}
          </div>
        </div>

        <div className="tab-actions" style={{ marginTop: '1.5rem' }}>
          <button 
            type="button" 
            className="danger-button" 
            onClick={excluirAgente} 
            disabled={saving}
          >
            {saving ? 'Aguarde…' : 'Excluir agente'}
          </button>
          <button 
            type="button" 
            className="save-button" 
            onClick={salvarPerfil} 
            disabled={saving}
          >
            {saving ? 'Salvando...' : 'Salvar perfil'}
          </button>
        </div>
      </div>
    );
  };

  /**
   * Renderiza aba Instruções (Halo Tools) - Etapa 2
   */
  const renderHaloInstrucoes = () => {
    // Detectar se está editando (fora do modal) ou criando (dentro do modal)
    // Se modalAberto === true, sempre usar haloForm (criação)
    // Se modalAberto === false e agenteSelecionado, usar formData (edição)
    const isCreatingInModal = modalAberto;
    const isEditing = agenteSelecionado && !modalAberto;
    const currentForm = isCreatingInModal ? haloForm : (isEditing ? formData : haloForm);
    const setCurrentForm = isCreatingInModal ? setHaloForm : (isEditing ? setFormData : setHaloForm);
    
    return (
      <div className="tab-content">
        <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ color: '#9bb3c7', fontWeight: 600 }}>Etapa 2 · Instruções do agente</div>
            <div style={{ color: '#a8c0d9', fontSize: '0.9rem', marginTop: 4 }}>Preencha as orientações do agente.</div>
          </div>
          <button
            type="button"
            onClick={() => setShowAiGeneratorModal(true)}
            style={{
              padding: '0.5rem 1rem',
              background: 'linear-gradient(135deg, #44e1ff, #b43bea)',
              border: 'none',
              borderRadius: '8px',
              color: '#ffffff',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.9rem',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 12px rgba(68, 225, 255, 0.3)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(68, 225, 255, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(68, 225, 255, 0.3)';
            }}
          >
            <AppIcon name="auto_awesome" style={{ fontSize: '1.2rem' }} />
            Gerar com IA
          </button>
        </div>
        
        <div className="form-field">
          <label style={{ color: '#ffffff' }}>Personalidade</label>
          <textarea 
            style={{ borderColor: 'var(--secondary-color)' }} 
            rows={4} 
            value={currentForm.personalidade || ''} 
            onChange={e => setCurrentForm(p => ({ ...p, personalidade: e.target.value }))} 
            placeholder="Descreva a personalidade do agente" 
          />
        </div>
        
        <div className="form-field">
          <label style={{ color: '#ffffff' }}>Contexto</label>
          <textarea 
            style={{ borderColor: 'var(--secondary-color)' }} 
            rows={4} 
            value={currentForm.contexto || ''} 
            onChange={e => setCurrentForm(p => ({ ...p, contexto: e.target.value }))} 
            placeholder="Explique o contexto de negócio e regras" 
          />
        </div>
        
        <div className="form-field">
          <label style={{ color: '#ffffff' }}>Direcionamento</label>
          <textarea 
            style={{ borderColor: 'var(--secondary-color)' }} 
            rows={4} 
            value={currentForm.direcionamento || ''} 
            onChange={e => setCurrentForm(p => ({ ...p, direcionamento: e.target.value }))} 
            placeholder="Como o agente deve agir/priorizar" 
          />
        </div>
        
        <div className="form-field">
          <label style={{ color: '#ffffff' }}>Restrições</label>
          <textarea 
            style={{ borderColor: 'var(--secondary-color)' }} 
            rows={4} 
            value={currentForm.restricoes || ''} 
            onChange={e => setCurrentForm(p => ({ ...p, restricoes: e.target.value }))} 
            placeholder="O que o agente não pode fazer/dizer" 
          />
        </div>
        
        <div className="form-field">
          <label style={{ color: '#ffffff' }}>Objetivos</label>
          <textarea 
            style={{ borderColor: 'var(--secondary-color)' }} 
            rows={4} 
            value={currentForm.objetivos || ''} 
            onChange={e => setCurrentForm(p => ({ ...p, objetivos: e.target.value }))} 
            placeholder="Metas que o agente deve alcançar" 
          />
        </div>
        
        <div className="form-field">
          <label style={{ color: '#ffffff' }}>Informações Gerais</label>
          <textarea 
            style={{ borderColor: 'var(--secondary-color)' }} 
            rows={4} 
            value={currentForm.informacoesGerais || ''} 
            onChange={e => setCurrentForm(p => ({ ...p, informacoesGerais: e.target.value }))} 
            placeholder="Detalhes complementares, glossário, exceções" 
          />
        </div>
        
        <div className="form-field">
          <label style={{ color: '#ffffff' }}>🔧 Instruções de Ferramentas</label>
          <textarea 
            style={{ borderColor: 'var(--secondary-color)' }} 
            rows={4} 
            value={currentForm.instrucoesFerramentas || ''} 
            onChange={e => setCurrentForm(p => ({ ...p, instrucoesFerramentas: e.target.value }))} 
            placeholder="Instruções específicas para uso das ferramentas (catálogos, formulários, etc.)" 
          />
          <small style={{ color: '#9bb3c7', marginTop: '0.25rem', display: 'block' }}>
            Gerado automaticamente na criação do agente. Edite conforme necessário.
          </small>
        </div>
        
        <div className="form-field">
          <label style={{ color: '#ffffff' }}>Descrição (opcional)</label>
          <textarea 
            style={{ borderColor: 'var(--secondary-color)' }} 
            rows={3} 
            value={currentForm.descricaoInstrucao || ''} 
            onChange={e => setCurrentForm(p => ({ ...p, descricaoInstrucao: e.target.value }))} 
            placeholder="Observações gerais sobre as instruções" 
          />
        </div>

        <div className="tab-actions" style={{ marginTop: '1.5rem' }}>
          <button 
            type="button" 
            className="danger-button" 
            onClick={excluirAgente} 
            disabled={saving}
          >
            {saving ? 'Aguarde…' : 'Excluir agente'}
          </button>
          <button 
            type="button" 
            className="save-button" 
            onClick={salvarInstrucoes} 
            disabled={saving}
          >
            {saving ? 'Salvando...' : 'Salvar instruções'}
          </button>
        </div>
      </div>
    );
  };

  /**
   * Renderiza aba Ferramentas (Halo Tools) - Etapa 3
   */
  const renderHaloFerramentas = () => {
    return (
      <div className="tab-content">
        <div style={{ marginBottom: 12, color: '#9bb3c7', fontWeight: 600 }}>Etapa 3 · Ferramentas</div>
        
        <div className="form-field">
          <label style={{ color: '#ffffff' }}>Selecionar ferramenta</label>
          <select value={haloToolChoice} onChange={e => setHaloToolChoice(e.target.value)}>
            <option value="">Nenhuma</option>
            <option value="produtos">Produtos</option>
          </select>
          <small style={{ color: '#9bb3c7' }}>As ferramentas estão sempre ativadas quando configuradas.</small>
        </div>

        {haloToolChoice === 'produtos' && (
          <div className="tool-panel" style={{ marginTop: 12, padding: 12, border: '1px solid var(--secondary-color)', borderRadius: 10 }}>
            <h4 style={{ margin: '0 0 12px', color: '#ffffff', display: 'flex', alignItems: 'center', gap: 8 }}>
              <AppIcon name="inventory_2" style={{ color: '#44e1ff' }} />
              Categorias de Produtos
            </h4>
            
            {loadingCategorias ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#9bb3c7' }}>
                <AppIcon name="sync" className="spinning" style={{ fontSize: '2rem', marginBottom: '0.5rem' }} />
                <p>Carregando categorias...</p>
              </div>
            ) : errorCategorias ? (
              <div style={{ padding: '1rem', background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: 8, color: '#fca5a5' }}>
                <AppIcon name="error" style={{ verticalAlign: 'middle', marginRight: 8 }} />
                {errorCategorias}
              </div>
            ) : categoriasDisponiveis.length === 0 ? (
              <div style={{ padding: '2rem', textAlign: 'center' }}>
                <AppIcon name="category" style={{ fontSize: '3rem', color: '#9bb3c7', marginBottom: '1rem' }} />
                <p style={{ color: '#9bb3c7', marginBottom: '1rem' }}>
                  Nenhuma categoria com produtos encontrada.
                </p>
                <p style={{ color: '#a8c0d9', fontSize: '0.9rem' }}>
                  Crie categorias e adicione produtos primeiro.
                </p>
              </div>
            ) : (
              <>
                <p style={{ color: '#9bb3c7', marginBottom: '1rem', fontSize: '0.9rem' }}>
                  Selecione as categorias que o agente poderá consultar:
                </p>
                <div style={{ display: 'grid', gap: 8, maxHeight: '400px', overflowY: 'auto', padding: '4px' }}>
                  {categoriasDisponiveis.map(cat => {
                    const isSelected = categoriasSelecionadas.includes(cat.id);
                    return (
                      <div
                        key={cat.id}
                        onClick={() => {
                          if (isSelected) {
                            setCategoriasSelecionadas(prev => prev.filter(id => id !== cat.id));
                          } else {
                            setCategoriasSelecionadas(prev => [...prev, cat.id]);
                          }
                        }}
                        style={{
                          padding: '12px',
                          border: `2px solid ${isSelected ? 'var(--primary-color)' : 'var(--secondary-color)'}`,
                          borderRadius: 8,
                          background: isSelected ? 'rgba(180, 59, 234, 0.1)' : 'transparent',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <div>
                          <div style={{ color: '#ffffff', fontWeight: 500, marginBottom: 4 }}>{cat.nome}</div>
                          <div style={{ color: '#9bb3c7', fontSize: '0.85rem' }}>
                            {cat.total_produtos} {cat.total_produtos === 1 ? 'produto' : 'produtos'}
                          </div>
                        </div>
                        {isSelected && (
                          <AppIcon name="check_circle" style={{ color: 'var(--primary-color)' }} />
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
            
            {/* ✅ NOVO: Toggle para Carousel do WhatsApp */}
            {categoriasSelecionadas.length > 0 && (
              <div style={{ marginTop: '1.5rem', padding: '1rem', background: 'rgba(68, 225, 255, 0.1)', border: '1px solid rgba(68, 225, 255, 0.3)', borderRadius: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <AppIcon name="shopping_cart" style={{ color: '#44e1ff', fontSize: '1.3rem' }} />
                    <label style={{ color: '#ffffff', fontWeight: 500, margin: 0, cursor: 'pointer' }}>
                      📱 Enviar via Carousel do WhatsApp
                    </label>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={enableWhatsappCatalog}
                    onClick={() => setEnableWhatsappCatalog(!enableWhatsappCatalog)}
                    style={{
                      width: '48px',
                      height: '26px',
                      borderRadius: '13px',
                      border: '1px solid ' + (enableWhatsappCatalog ? 'rgba(68, 225, 255, 0.5)' : 'rgba(255,255,255,0.2)'),
                      background: enableWhatsappCatalog ? 'linear-gradient(135deg, #44e1ff, #3b82f6)' : 'rgba(0,0,0,0.25)',
                      position: 'relative',
                      cursor: 'pointer',
                      padding: 0,
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <span
                      style={{
                        position: 'absolute',
                        top: '2px',
                        left: enableWhatsappCatalog ? '24px' : '2px',
                        width: '22px',
                        height: '22px',
                        borderRadius: '50%',
                        background: '#fff',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }}
                    />
                  </button>
                </div>
                
                {enableWhatsappCatalog && (
                  <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: 'rgba(68, 225, 255, 0.05)', borderRadius: 6, borderLeft: '3px solid #44e1ff' }}>
                    <p style={{ margin: '0 0 0.5rem 0', color: '#44e1ff', fontSize: '0.9rem', fontWeight: 500 }}>✨ Carousel habilitado!</p>
                    <ul style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.2rem', color: '#a8c0d9', fontSize: '0.85rem', lineHeight: '1.5' }}>
                      <li>Produtos serão enviados em formato visual e interativo</li>
                      <li>Cliente pode deslizar e clicar em "Ver Detalhes"</li>
                      <li>Sua mensagem aparecerá acima do Carousel</li>
                      <li>Todos os produtos da categoria serão inclusos</li>
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Formulário - Centralizado no Projeto CRM */}
        <div style={{ marginTop: 16, padding: '1rem', background: 'rgba(68, 225, 255, 0.08)', border: '1px solid rgba(68, 225, 255, 0.25)', borderRadius: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <AppIcon name="description" style={{ color: '#44e1ff' }} />
            <strong style={{ color: '#44e1ff' }}>Formulário de Coleta</strong>
          </div>
          <p style={{ margin: 0, color: '#9bb3c7', fontSize: '0.9rem', lineHeight: '1.5' }}>
            O formulário é configurado diretamente no <strong>Projeto CRM</strong> vinculado a este agente, na lista "Equipe de Vendas".
          </p>
        </div>

        <div className="tab-actions" style={{ marginTop: '1.5rem' }}>
          <button 
            type="button" 
            className="danger-button" 
            onClick={excluirAgente} 
            disabled={saving}
          >
            {saving ? 'Aguarde…' : 'Excluir agente'}
          </button>
          <button 
            type="button" 
            className="save-button" 
            onClick={salvarFerramentas} 
            disabled={saving}
          >
            {saving ? 'Salvando...' : 'Salvar ferramentas'}
          </button>
        </div>
      </div>
    );
  };

  /**
   * Renderiza aba Config (Halo Tools) - Etapa 4
   */
  const renderHaloConfig = () => {
    return (
      <div className="tab-content">
        <div style={{ marginBottom: 12, color: '#9bb3c7', fontWeight: 600 }}>Etapa 4 · Configurações</div>
        
        <div className="form-grid-2">
          <div className="form-field">
            <label style={{ marginBottom: 2, display: 'flex', alignItems: 'center', gap: 6 }}>
              Temperatura
              <AppIcon name="info" style={{ fontSize: '1rem', color: '#9bb3c7', cursor: 'help' }} />
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <input 
                type="range" 
                min={0.1} 
                max={1.0} 
                step={0.1} 
                value={formData.temperatura || 0.7} 
                onChange={e => setFormData(p => ({ ...p, temperatura: Number(e.target.value) }))} 
                style={{ flex: 1 }} 
              />
              <span style={{ color: '#a8c0d9', width: 36, textAlign: 'right' }}>{(formData.temperatura || 0.7).toFixed(1)}</span>
            </div>
          </div>
          <div className="form-field">
            <label style={{ marginBottom: 2 }}>Habilitar agente</label>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: '#ffffff' }}>{formData.agenteEnabled ? 'Ativo' : 'Inativo'}</span>
              <button 
                type="button" 
                role="switch" 
                aria-checked={formData.agenteEnabled} 
                onClick={() => setFormData(p => ({ ...p, agenteEnabled: !p.agenteEnabled }))}
                style={{ width: 48, height: 26, borderRadius: 13, border: '1px solid var(--secondary-color)', background: formData.agenteEnabled ? 'var(--primary-color)' : 'rgba(0,0,0,0.25)', position: 'relative', cursor: 'pointer', padding: 0 }}
              >
                <span style={{ position: 'absolute', top: 2, left: formData.agenteEnabled ? 24 : 2, width: 22, height: 22, borderRadius: '50%', background: '#fff', transition: 'left 0.18s ease' }} />
              </button>
            </div>
          </div>
        </div>
        
        <div className="form-field" style={{ marginTop: 12 }}>
          <label style={{ marginBottom: 2, display: 'flex', alignItems: 'center', gap: 6 }}>
            Agrupar mensagens
            <AppIcon name="info" style={{ fontSize: '1rem', color: '#9bb3c7', cursor: 'help' }} />
          </label>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: '#ffffff' }}>{formData.agruparEnabled ? 'Ativo' : 'Inativo'}</span>
            <button 
              type="button" 
              role="switch" 
              aria-checked={formData.agruparEnabled} 
              onClick={() => setFormData(p => ({ ...p, agruparEnabled: !p.agruparEnabled }))}
              style={{ width: 48, height: 26, borderRadius: 13, border: '1px solid var(--secondary-color)', background: formData.agruparEnabled ? 'var(--primary-color)' : 'rgba(0,0,0,0.25)', position: 'relative', cursor: 'pointer', padding: 0 }}
            >
              <span style={{ position: 'absolute', top: 2, left: formData.agruparEnabled ? 24 : 2, width: 22, height: 22, borderRadius: '50%', background: '#fff', transition: 'left 0.18s ease' }} />
            </button>
          </div>
          {formData.agruparEnabled && (
            <div className="form-field" style={{ marginTop: 12 }}>
              <label style={{ marginBottom: 2 }}>Tempo para agrupar (segundos)</label>
              <input 
                type="number" 
                min={0} 
                step={1} 
                placeholder="Ex.: 30" 
                value={formData.agruparSegundos || ''} 
                onChange={e => setFormData(p => ({ ...p, agruparSegundos: e.target.value }))} 
              />
              <small style={{ color: '#9bb3c7', fontSize: '0.85rem', marginTop: '0.25rem', display: 'block' }}>0 = desabilitado</small>
            </div>
          )}
        </div>

        <div className="tab-actions" style={{ marginTop: '1.5rem' }}>
          <button 
            type="button" 
            className="danger-button" 
            onClick={excluirAgente} 
            disabled={saving}
          >
            {saving ? 'Aguarde…' : 'Excluir agente'}
          </button>
          <button 
            type="button" 
            className="save-button" 
            onClick={salvarConfiguracoes} 
            disabled={saving}
          >
            {saving ? 'Salvando...' : 'Salvar configurações'}
          </button>
        </div>
      </div>
    );
  };

  /**
   * Função para gerar instruções com IA
   */
  const handleGenerateWithAI = async () => {
    if (!aiDescricaoFuncao.trim()) {
      setAiGeneratorError('Por favor, descreva a função do agente.');
      return;
    }

    try {
      setAiGeneratorLoading(true);
      setAiGeneratorError('');

      // Buscar configurações da empresa
      const empresaId = getCompanyId();
      if (!empresaId) {
        throw new Error('Empresa não identificada');
      }

      const { data: empresa, error: empresaError } = await supabase
        .from('configuracoes_empresa')
        .select('*')
        .eq('company_id', empresaId)
        .single();

      if (empresaError) throw empresaError;

      // Detectar qual formulário usar (modal de criação ou edição de agente)
      const isEditingAgent = agenteSelecionado && !modalAberto;
      const form = isEditingAgent ? formData : haloForm;

      // Preparar dados para enviar ao backend
      const requestData = {
        empresa: {
          nome_fantasia: empresa.nome_fantasia || '',
          segmento: empresa.segmento || '',
          subsegmento: empresa.subsegmento || '',
          descricao: empresa.descricao || '',
          cidade: empresa.cidade || '',
          estado: empresa.estado || '',
          telefone_principal: empresa.telefone_principal || ''
        },
        agente: {
          nome: form.nome,
          descricao: form.descricao,
          canal: form.canal,
          funcaoPrincipal: form.funcaoPrincipal,
          subFuncao: form.subFuncao,
          descricaoFuncao: aiDescricaoFuncao
        }
      };

      // Chamar backend real
      const response = await fetch(`${BACKEND_NODE_URL}/api/ai-automation/generate-prompt`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          companyId: empresaId,
          agente: requestData.agente
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        // Mensagem específica para timeout
        if (response.status === 504) {
          throw new Error('A geração está demorando muito. Tente novamente com uma descrição mais curta ou aguarde alguns segundos.');
        }
        
        throw new Error(errorData.error || `Erro na API: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Erro ao gerar instruções');
      }


      // Preencher os campos do formulário correto com a resposta da IA
      if (isEditingAgent) {
        // Editando agente existente - usar formData
        setFormData(prev => ({
          ...prev,
          personalidade: result.prompt.personalidade || '',
          contexto: result.prompt.contexto || '',
          direcionamento: result.prompt.direcionamento || '',
          restricoes: result.prompt.restricoes || '',
          objetivos: result.prompt.objetivos || '',
          informacoesGerais: result.prompt.informacoes_gerais || result.prompt.informacoesGerais || ''
        }));
      } else {
        // Criando novo agente - usar haloForm
        const newValues = {
          ...haloForm,
          personalidade: result.prompt.personalidade || '',
          contexto: result.prompt.contexto || '',
          direcionamento: result.prompt.direcionamento || '',
          restricoes: result.prompt.restricoes || '',
          objetivos: result.prompt.objetivos || '',
          informacoesGerais: result.prompt.informacoes_gerais || result.prompt.informacoesGerais || ''
        };
        setHaloForm(newValues);
      }

      // Fechar modal e limpar
      setShowAiGeneratorModal(false);
      setAiDescricaoFuncao('');
      setAiGeneratorError('');

    } catch (error) {
      console.error('Erro ao gerar com IA:', error);
      setAiGeneratorError(error.message || 'Erro ao gerar instruções. Tente novamente.');
    } finally {
      setAiGeneratorLoading(false);
    }
  };

  /**
   * Renderiza modal de criação de agente
   */
  const renderModal = () => {
    if (!modalAberto) return null;

    return (
      <div
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
        onClick={() => setModalAberto(false)}
      >
        <div
          key={`${modelChoice}-${haloStep}`}
          style={{ width: '95%', maxWidth: 1000, maxHeight: '92vh', overflow: 'hidden', background: 'var(--card-background)', border: '1px solid var(--secondary-color)', borderRadius: 10, boxShadow: '0 10px 30px rgba(0,0,0,0.4)', display: 'flex', flexDirection: 'column' }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: 'var(--card-background)', borderBottom: '1px solid var(--secondary-color)' }}>
            <h3 style={{ margin: 0 }}>Novo Agente</h3>
            <button onClick={() => setModalAberto(false)} style={{ background: 'transparent', border: 'none', color: 'var(--text-color)', fontSize: 22, cursor: 'pointer' }}>×</button>
          </div>

          {/* Área rolável principal do conteúdo das etapas */}
          <div key={haloStep} style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            {(() => {
              // Escolha inicial
              if (!modelChoice) {
                return (
                  <div style={{ padding: '1rem 1.2rem', borderBottom: '1px solid var(--secondary-color)' }}>
                    <h4 style={{ margin: '0 0 8px', color: '#ffffff' }}>Escolha o modelo</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
                      <button type="button" onClick={() => { setModelChoice('nano'); setHaloStep(1); }} aria-pressed={modelChoice === 'nano'}
                        style={{ textAlign: 'left', padding: 16, borderRadius: 10, border: `1px solid #10b981`, background: 'rgba(16, 185, 129, 0.08)', cursor: 'pointer', color: '#ffffff' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <AppIcon name="speed" style={{ color: '#10b981' }} />
                          <strong style={{ color: '#ffffff' }}>HALO Nano 1.0</strong>
                        </div>
                        <div style={{ marginTop: 6, color: '#ffffff' }}>Melhor custo-benefício. Ideal para atendimento, suporte e gestão.</div>
                      </button>
                      <button type="button" disabled
                        style={{ textAlign: 'left', padding: 16, borderRadius: 10, border: `1px solid var(--secondary-color)`, background: 'transparent', cursor: 'not-allowed', color: '#ffffff', opacity: 0.5, position: 'relative' }}>
                        <div style={{ position: 'absolute', top: 8, right: 8, background: 'linear-gradient(135deg, #f59e0b, #ef4444)', color: '#fff', padding: '4px 10px', borderRadius: 12, fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Em Breve</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <AppIcon name="stars" style={{ color: '#44e1ff' }} />
                          <strong style={{ color: '#ffffff' }}>Halo Pro 1.0</strong>
                        </div>
                        <div style={{ marginTop: 6, color: '#ffffff' }}>RAG, ferramentas e configurações avançadas.</div>
                      </button>
                      <button type="button" onClick={() => setModelChoice('webhook')} aria-pressed={modelChoice === 'webhook'}
                        style={{ textAlign: 'left', padding: 16, borderRadius: 10, border: `1px solid var(--secondary-color)`, background: 'transparent', cursor: 'pointer', color: '#ffffff' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <AppIcon name="hub" style={{ color: '#44e1ff' }} />
                          <strong style={{ color: '#ffffff' }}>Aberto (webhook)</strong>
                        </div>
                        <div style={{ marginTop: 6, color: '#ffffff' }}>Integração com N8N, Zapier, Make, etc.</div>
                      </button>
                    </div>
                  </div>
                );
              }

              // HALO Nano 1.0 - Mesmas etapas do Halo Tools
              if (modelChoice === 'nano' && haloStep === 1) {
                return (
                  <div style={{ padding: '1rem 1.2rem', overflowY: 'auto' }}>
                    <div style={{ marginBottom: 12, color: '#9bb3c7' }}>Etapa 1 de 4 · Configurações iniciais</div>
                    <div className="form-grid-2">
                      <div className="form-field">
                        <label>Nome do agente</label>
                        <input type="text" value={haloForm.nome} onChange={e => setHaloForm(p => ({ ...p, nome: e.target.value }))} placeholder="Ex.: Halo Vendas" />
                      </div>
                      <div className="form-field">
                        <label>Conexão</label>
                        <select value={haloForm.canal} onChange={e => setHaloForm(p => ({ ...p, canal: e.target.value }))}>
                          <option value="Interno">Interno</option>
                          <option value="WhatsApp">WhatsApp</option>
                          <option value="Telegram">Telegram</option>
                        </select>
                      </div>
                    </div>

                    {haloForm.canal === 'Interno' && (
                      <div className="hint" style={{ color: '#a8c0d9', marginBottom: 8 }}>Nenhuma configuração adicional para conexão interna.</div>
                    )}
                    
                    {haloForm.canal === 'WhatsApp' && (
                      <div className="form-field" style={{ marginTop: 8 }}>
                        <label>Selecionar conexão WhatsApp</label>
                        <select 
                          value={haloForm.whatsappConnectionId || ''} 
                          onChange={e => setHaloForm(p => ({ ...p, whatsappConnectionId: e.target.value }))}
                        >
                          <option value="">Selecione uma conexão...</option>
                          {whatsappConnections.map(conn => {
                            const nome = conn.name || 'Sem nome';
                            const numero = conn.number ? ` - ${conn.number}` : '';
                            return (
                              <option key={conn.id} value={conn.id}>
                                {nome}{numero}
                              </option>
                            );
                          })}
                        </select>
                        {whatsappConnections.length === 0 && (
                          <small style={{ color: '#fca5a5' }}>Nenhuma conexão WhatsApp disponível. Configure uma conexão primeiro.</small>
                        )}
                      </div>
                    )}
                    
                    {haloForm.canal === 'Telegram' && (
                      <div className="hint" style={{ color: '#a8c0d9', marginBottom: 8 }}>Integração com Telegram: em breve.</div>
                    )}

                    <div className="form-grid-2" style={{ marginTop: 8 }}>
                      <div className="form-field">
                        <label>Função principal</label>
                        <select value={haloForm.funcaoPrincipal} onChange={e => setHaloForm(p => ({ ...p, funcaoPrincipal: e.target.value, subFuncao: '' }))}>
                          <option value="">Selecione...</option>
                          <option value="atendimento">Atendimento ao cliente</option>
                          <option value="suporte_interno">Suporte interno</option>
                        </select>
                      </div>
                      <div className="form-field">
                        <label>Subopções</label>
                        {haloForm.funcaoPrincipal === 'atendimento' && (
                          <select value={haloForm.subFuncao} onChange={e => {
                            const subFuncao = e.target.value;
                            const temperatura = 0.5;
                            setHaloForm(p => ({ ...p, subFuncao, temperatura }));
                          }}>
                            <option value="">Selecione...</option>
                            <option value="vendas">Vendas</option>
                            <option value="suporte">Suporte</option>
                          </select>
                        )}
                        {haloForm.funcaoPrincipal === 'suporte_interno' && (
                          <select value={haloForm.subFuncao} onChange={e => {
                            const subFuncao = e.target.value;
                            let temperatura = 0.6;
                            if (subFuncao === 'especialista') temperatura = 0.7;
                            setHaloForm(p => ({ ...p, subFuncao, temperatura }));
                          }}>
                            <option value="">Selecione...</option>
                            <option value="gestao">Gestão</option>
                            <option value="treinamento">Treinamento</option>
                            <option value="especialista">Especialista</option>
                          </select>
                        )}
                        {!haloForm.funcaoPrincipal && (
                          <input type="text" disabled placeholder="Selecione a função principal primeiro" style={{ opacity: 0.5 }} />
                        )}
                      </div>
                    </div>
                  </div>
                );
              }

              if (modelChoice === 'nano' && haloStep === 2) {
                return (
                  <div style={{ padding: '1rem 1.2rem', overflowY: 'auto' }}>
                    <h4 style={{ margin: '0 0 12px', color: '#10b981' }}>Etapa 2: Instruções</h4>
                    {renderHaloInstrucoes()}
                  </div>
                );
              }

              if (modelChoice === 'nano' && haloStep === 3) {
                return (
                  <div style={{ padding: '1rem 1.2rem' }}>
                    <h4 style={{ margin: '0 0 12px', color: '#10b981' }}>Etapa 3: Ferramentas</h4>
                    {renderHaloFerramentas()}
                  </div>
                );
              }

              if (modelChoice === 'nano' && haloStep === 4) {
                return (
                  <div style={{ padding: '1rem 1.2rem' }}>
                    <h4 style={{ margin: '0 0 12px', color: '#10b981' }}>Etapa 4: Configurações Finais</h4>
                    {renderHaloConfig()}
                  </div>
                );
              }

              // Webhook - Em breve
              if (modelChoice === 'webhook') {
                return (
                  <div style={{ padding: '2rem', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '300px', textAlign: 'center' }}>
                    <AppIcon name="construction" style={{ fontSize: '4rem', color: '#44e1ff', marginBottom: '1rem' }} />
                    <h3 style={{ margin: '0 0 0.5rem 0', color: '#ffffff', fontSize: '1.5rem' }}>Aberto (webhook)</h3>
                    <p style={{ color: '#9bb3c7', fontSize: '1rem', marginBottom: '2rem', maxWidth: '400px' }}>
                      Integração com N8N, Zapier, Make e outras ferramentas estará disponível em breve.
                    </p>
                    <button 
                      type="button" 
                      onClick={() => setModelChoice(null)}
                      className="cancel-button"
                    >
                      <AppIcon name="arrow_back" />
                      Voltar
                    </button>
                  </div>
                );
              }

              // Halo Pro - Etapa 1 (desativado)
              if (modelChoice === 'pro' && haloStep === 1) {
                return (
                  <div style={{ padding: '1rem 1.2rem', overflowY: 'auto' }}>
                    <div style={{ marginBottom: 12, color: '#9bb3c7' }}>Etapa 1 de 4 · Configurações iniciais</div>
                    <div className="form-grid-2">
                      <div className="form-field">
                        <label>Nome do agente</label>
                        <input type="text" value={haloForm.nome} onChange={e => setHaloForm(p => ({ ...p, nome: e.target.value }))} placeholder="Ex.: Halo Vendas" />
                      </div>
                      <div className="form-field">
                        <label>Conexão</label>
                        <select value={haloForm.canal} onChange={e => setHaloForm(p => ({ ...p, canal: e.target.value }))}>
                          <option value="Interno">Interno</option>
                          <option value="WhatsApp">WhatsApp</option>
                          <option value="Telegram">Telegram</option>
                        </select>
                      </div>
                    </div>

                    {haloForm.canal === 'Interno' && (
                      <div className="hint" style={{ color: '#a8c0d9', marginBottom: 8 }}>Nenhuma configuração adicional para conexão interna.</div>
                    )}
                    
                    {haloForm.canal === 'WhatsApp' && (
                      <div className="form-field" style={{ marginTop: 8 }}>
                        <label>Selecionar conexão WhatsApp</label>
                        <select 
                          value={haloForm.whatsappConnectionId || ''} 
                          onChange={e => setHaloForm(p => ({ ...p, whatsappConnectionId: e.target.value }))}
                        >
                          <option value="">Selecione uma conexão...</option>
                          {whatsappConnections.map(conn => {
                            const nome = conn.name || 'Sem nome';
                            const numero = conn.number ? ` - ${conn.number}` : '';
                            return (
                              <option key={conn.id} value={conn.id}>
                                {nome}{numero}
                              </option>
                            );
                          })}
                        </select>
                        {whatsappConnections.length === 0 && (
                          <small style={{ color: '#fca5a5' }}>Nenhuma conexão WhatsApp disponível. Configure uma conexão primeiro.</small>
                        )}
                      </div>
                    )}
                    
                    {haloForm.canal === 'Telegram' && (
                      <div className="hint" style={{ color: '#a8c0d9', marginBottom: 8 }}>Integração com Telegram: em breve.</div>
                    )}

                    <div className="form-grid-2" style={{ marginTop: 8 }}>
                      <div className="form-field">
                        <label>Função principal</label>
                        <select value={haloForm.funcaoPrincipal} onChange={e => setHaloForm(p => ({ ...p, funcaoPrincipal: e.target.value, subFuncao: '' }))}>
                          <option value="">Selecione...</option>
                          <option value="atendimento">Atendimento ao cliente</option>
                          <option value="suporte_interno">Suporte interno</option>
                        </select>
                      </div>
                      <div className="form-field">
                        <label>Subopções</label>
                        {haloForm.funcaoPrincipal === 'atendimento' && (
                          <select value={haloForm.subFuncao} onChange={e => {
                            const subFuncao = e.target.value;
                            const temperatura = 0.5; // Atendimento ao cliente sempre 0.5
                            
                            setHaloForm(p => ({ ...p, subFuncao, temperatura }));
                          }}>
                            <option value="">Selecione...</option>
                            <option value="vendas">Vendas</option>
                            <option value="suporte">Suporte</option>
                          </select>
                        )}
                        {haloForm.funcaoPrincipal === 'suporte_interno' && (
                          <select value={haloForm.subFuncao} onChange={e => {
                            const subFuncao = e.target.value;
                            let temperatura = 0.6; // Padrão para gestão e treinamento
                            
                            if (subFuncao === 'especialista') {
                              temperatura = 0.7;
                            }
                            
                            setHaloForm(p => ({ ...p, subFuncao, temperatura }));
                          }}>
                            <option value="">Selecione...</option>
                            <option value="gestao">Gestão</option>
                            <option value="treinamento">Treinamento</option>
                            <option value="especialista">Especialista</option>
                          </select>
                        )}
                        {!haloForm.funcaoPrincipal && (
                          <select disabled>
                            <option>Escolha uma função principal primeiro</option>
                          </select>
                        )}
                      </div>
                    </div>
                  </div>
                );
              }

              // Halo Pro - Etapa 2 (desativado)
              if (modelChoice === 'pro' && haloStep === 2) {
                return (
                  <div style={{ padding: '1rem 1.2rem', overflowY: 'auto' }}>
                    <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ color: '#9bb3c7', fontWeight: 600 }}>Etapa 2 de 4 · Instruções do agente</div>
                      <button
                        type="button"
                        onClick={() => setShowAiGeneratorModal(true)}
                        style={{
                          padding: '0.5rem 1rem',
                          background: 'linear-gradient(135deg, #44e1ff, #b43bea)',
                          border: 'none',
                          borderRadius: '8px',
                          color: '#ffffff',
                          fontWeight: 600,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          fontSize: '0.9rem',
                          transition: 'all 0.3s ease',
                          boxShadow: '0 4px 12px rgba(68, 225, 255, 0.3)'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 6px 16px rgba(68, 225, 255, 0.4)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(68, 225, 255, 0.3)';
                        }}
                      >
                        <AppIcon name="auto_awesome" style={{ fontSize: '1.2rem' }} />
                        Gerar com IA
                      </button>
                    </div>
                    <div className="form-field">
                      <label style={{ color: '#ffffff' }}>Personalidade</label>
                      <textarea style={{ borderColor: 'var(--secondary-color)' }} rows={4} value={haloForm.personalidade} onChange={e => setHaloForm(p => ({ ...p, personalidade: e.target.value }))} placeholder="Descreva a personalidade do agente" />
                    </div>
                    <div className="form-field">
                      <label style={{ color: '#ffffff' }}>Contexto</label>
                      <textarea style={{ borderColor: 'var(--secondary-color)' }} rows={4} value={haloForm.contexto} onChange={e => setHaloForm(p => ({ ...p, contexto: e.target.value }))} placeholder="Explique o contexto de negócio e regras" />
                    </div>
                    <div className="form-field">
                      <label style={{ color: '#ffffff' }}>Direcionamento</label>
                      <textarea style={{ borderColor: 'var(--secondary-color)' }} rows={4} value={haloForm.direcionamento} onChange={e => setHaloForm(p => ({ ...p, direcionamento: e.target.value }))} placeholder="Como o agente deve agir/priorizar" />
                    </div>
                    <div className="form-field">
                      <label style={{ color: '#ffffff' }}>Restrições</label>
                      <textarea style={{ borderColor: 'var(--secondary-color)' }} rows={4} value={haloForm.restricoes} onChange={e => setHaloForm(p => ({ ...p, restricoes: e.target.value }))} placeholder="O que o agente não pode fazer/dizer" />
                    </div>
                    <div className="form-field">
                      <label style={{ color: '#ffffff' }}>Objetivos</label>
                      <textarea style={{ borderColor: 'var(--secondary-color)' }} rows={4} value={haloForm.objetivos} onChange={e => setHaloForm(p => ({ ...p, objetivos: e.target.value }))} placeholder="Metas que o agente deve alcançar" />
                    </div>
                    <div className="form-field">
                      <label style={{ color: '#ffffff' }}>Informações Gerais</label>
                      <textarea style={{ borderColor: 'var(--secondary-color)' }} rows={4} value={haloForm.informacoesGerais} onChange={e => setHaloForm(p => ({ ...p, informacoesGerais: e.target.value }))} placeholder="Detalhes complementares, glossário, exceções" />
                    </div>
                    <div className="form-field">
                      <label style={{ color: '#ffffff' }}>Descrição (opcional)</label>
                      <textarea style={{ borderColor: 'var(--secondary-color)' }} rows={3} value={haloForm.descricaoInstrucao} onChange={e => setHaloForm(p => ({ ...p, descricaoInstrucao: e.target.value }))} placeholder="Observações gerais sobre as instruções" />
                    </div>
                  </div>
                );
              }

              // Halo Pro - Etapa 3 (desativado)
              if (modelChoice === 'pro' && haloStep === 3) {
                return (
                  <div style={{ padding: '1rem 1.2rem' }}>
                    <div style={{ marginBottom: 12, color: '#9bb3c7', fontWeight: 600 }}>Etapa 3 de 4 · Ferramentas</div>
                    
                    <div className="form-field">
                      <label style={{ color: '#ffffff' }}>Selecionar ferramenta</label>
                      <select value={haloToolChoice} onChange={e => setHaloToolChoice(e.target.value)}>
                        <option value="">Nenhuma (pular)</option>
                        <option value="produtos">Produtos</option>
                        <option value="formulario">Formulário CRM</option>
                      </select>
                      <small style={{ color: '#9bb3c7' }}>As ferramentas estão sempre ativadas quando configuradas.</small>
                    </div>

                    {haloToolChoice === 'produtos' && (
                      <div className="tool-panel" style={{ marginTop: 12, padding: 12, border: '1px solid var(--secondary-color)', borderRadius: 10 }}>
                        <h4 style={{ margin: '0 0 12px', color: '#ffffff', display: 'flex', alignItems: 'center', gap: 8 }}>
                          <AppIcon name="inventory_2" style={{ color: '#44e1ff' }} />
                          Categorias de Produtos
                        </h4>
                        
                        {loadingCategorias ? (
                          <div style={{ padding: '2rem', textAlign: 'center', color: '#9bb3c7' }}>
                            <AppIcon name="sync" className="spinning" style={{ fontSize: '2rem', marginBottom: '0.5rem' }} />
                            <p>Carregando categorias...</p>
                          </div>
                        ) : errorCategorias ? (
                          <div style={{ padding: '1rem', background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: 8, color: '#fca5a5' }}>
                            <AppIcon name="error" style={{ verticalAlign: 'middle', marginRight: 8 }} />
                            {errorCategorias}
                          </div>
                        ) : categoriasDisponiveis.length === 0 ? (
                          <div style={{ padding: '2rem', textAlign: 'center' }}>
                            <AppIcon name="category" style={{ fontSize: '3rem', color: '#9bb3c7', marginBottom: '1rem' }} />
                            <p style={{ color: '#9bb3c7', marginBottom: '1rem' }}>
                              Nenhuma categoria com produtos encontrada.
                            </p>
                            <p style={{ color: '#a8c0d9', fontSize: '0.9rem' }}>
                              Crie categorias e adicione produtos primeiro.
                            </p>
                          </div>
                        ) : (
                          <>
                            <p style={{ color: '#9bb3c7', marginBottom: '1rem', fontSize: '0.9rem' }}>
                              Selecione uma ou mais categorias:
                            </p>
                            <div style={{ display: 'grid', gap: 8, maxHeight: '400px', overflowY: 'auto', padding: '4px' }}>
                              {categoriasDisponiveis.map(cat => {
                                const isSelected = categoriasSelecionadas.includes(cat.id);
                                return (
                                  <div
                                    key={cat.id}
                                    onClick={() => {
                                      if (isSelected) {
                                        setCategoriasSelecionadas(prev => prev.filter(id => id !== cat.id));
                                      } else {
                                        setCategoriasSelecionadas(prev => [...prev, cat.id]);
                                      }
                                    }}
                                    style={{
                                      padding: '12px',
                                      border: `2px solid ${isSelected ? 'var(--primary-color)' : 'var(--secondary-color)'}`,
                                      borderRadius: 8,
                                      background: isSelected ? 'rgba(180, 59, 234, 0.1)' : 'transparent',
                                      cursor: 'pointer',
                                      transition: 'all 0.2s ease',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                    }}
                                  >
                                    <div>
                                      <div style={{ color: '#ffffff', fontWeight: 500, marginBottom: 4 }}>{cat.nome}</div>
                                      <div style={{ color: '#9bb3c7', fontSize: '0.85rem' }}>
                                        {cat.total_produtos} {cat.total_produtos === 1 ? 'produto' : 'produtos'}
                                      </div>
                                    </div>
                                    {isSelected && (
                                      <AppIcon name="check_circle" style={{ color: 'var(--primary-color)' }} />
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </>
                        )}
                      </div>
                    )}

                    {haloToolChoice === 'formulario' && (
                      <div className="tool-panel" style={{ marginTop: 12, padding: 12, border: '1px solid var(--secondary-color)', borderRadius: 10 }}>
                        <h4 style={{ margin: '0 0 12px', color: '#ffffff', display: 'flex', alignItems: 'center', gap: 8 }}>
                          <AppIcon name="description" style={{ color: '#b43bea' }} />
                          Projetos CRM com Formulário
                        </h4>
                        
                        {loadingFormularios ? (
                          <div style={{ padding: '2rem', textAlign: 'center', color: '#9bb3c7' }}>
                            <AppIcon name="sync" className="spinning" style={{ fontSize: '2rem', marginBottom: '0.5rem' }} />
                            <p>Carregando projetos...</p>
                          </div>
                        ) : errorFormularios ? (
                          <div style={{ padding: '1rem', background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: 8, color: '#fca5a5' }}>
                            <AppIcon name="error" style={{ verticalAlign: 'middle', marginRight: 8 }} />
                            {errorFormularios}
                          </div>
                        ) : formulariosDisponiveis.length === 0 ? (
                          <div style={{ padding: '2rem', textAlign: 'center' }}>
                            <AppIcon name="folder_off" style={{ fontSize: '3rem', color: '#9bb3c7', marginBottom: '1rem' }} />
                            <p style={{ color: '#9bb3c7', marginBottom: '1rem' }}>
                              Nenhum projeto CRM com formulário encontrado.
                            </p>
                            <p style={{ color: '#a8c0d9', fontSize: '0.9rem' }}>
                              Crie um projeto CRM e habilite o formulário nas configurações.
                            </p>
                          </div>
                        ) : (
                          <>
                            <p style={{ color: '#9bb3c7', marginBottom: '1rem', fontSize: '0.9rem' }}>
                              Selecione um ou mais projetos CRM:
                            </p>
                            <div style={{ display: 'grid', gap: 8, maxHeight: '400px', overflowY: 'auto', padding: '4px' }}>
                              {formulariosDisponiveis.map(projeto => {
                                const isSelected = formulariosSelecionados.includes(projeto.id);
                                const numCampos = projeto.settings?.crm?.formFields?.length || 0;
                                return (
                                  <div
                                    key={projeto.id}
                                    onClick={() => {
                                      if (isSelected) {
                                        setFormulariosSelecionados(prev => prev.filter(id => id !== projeto.id));
                                      } else {
                                        setFormulariosSelecionados(prev => [...prev, projeto.id]);
                                      }
                                    }}
                                    style={{
                                      padding: '12px',
                                      border: `2px solid ${isSelected ? '#b43bea' : 'var(--secondary-color)'}`,
                                      borderRadius: 8,
                                      background: isSelected ? 'rgba(180, 59, 234, 0.1)' : 'transparent',
                                      cursor: 'pointer',
                                      transition: 'all 0.2s ease',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                    }}
                                  >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                      <div style={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: 8,
                                        background: projeto.color || '#44e1ff',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0
                                      }}>
                                        <AppIcon name={projeto.icon || 'work'} style={{ color: '#ffffff', fontSize: '1.5rem' }} />
                                      </div>
                                      <div>
                                        <div style={{ color: '#ffffff', fontWeight: 500, marginBottom: 4 }}>{projeto.name}</div>
                                        <div style={{ color: '#9bb3c7', fontSize: '0.85rem' }}>
                                          {numCampos} {numCampos === 1 ? 'campo' : 'campos'} no formulário
                                        </div>
                                      </div>
                                    </div>
                                    {isSelected && (
                                      <AppIcon name="check_circle" style={{ color: '#b43bea' }} />
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              }

              // Halo Pro - Etapa 4 (desativado)
              if (modelChoice === 'pro' && haloStep === 4) {
                return (
                  <div style={{ padding: '1rem 1.2rem' }}>
                    <div style={{ marginBottom: 12, color: '#9bb3c7', fontWeight: 600 }}>Etapa 4 de 4 · Configurações</div>
                    <div className="form-grid-2">
                      <div className="form-field">
                        <label style={{ marginBottom: 2, display: 'flex', alignItems: 'center', gap: 6 }}>
                          Temperatura
                          <AppIcon name="info" style={{ fontSize: '1rem', color: '#9bb3c7', cursor: 'help' }} />
                        </label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <input 
                            type="range" 
                            min={0.1} 
                            max={1.0} 
                            step={0.1} 
                            value={haloForm.temperatura} 
                            onChange={e => setHaloForm(p => ({ ...p, temperatura: Number(e.target.value) }))} 
                            style={{ flex: 1 }} 
                          />
                          <span style={{ color: '#a8c0d9', width: 36, textAlign: 'right' }}>{haloForm.temperatura.toFixed(1)}</span>
                        </div>
                      </div>
                      <div className="form-field">
                        <label style={{ marginBottom: 2 }}>Habilitar agente</label>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ color: '#ffffff' }}>{haloForm.agenteEnabled ? 'Ativo' : 'Inativo'}</span>
                          <button type="button" role="switch" aria-checked={haloForm.agenteEnabled} onClick={() => setHaloForm(p => ({ ...p, agenteEnabled: !p.agenteEnabled }))}
                            style={{ width: 48, height: 26, borderRadius: 13, border: '1px solid var(--secondary-color)', background: haloForm.agenteEnabled ? 'var(--primary-color)' : 'rgba(0,0,0,0.25)', position: 'relative', cursor: 'pointer', padding: 0 }}>
                            <span style={{ position: 'absolute', top: 2, left: haloForm.agenteEnabled ? 24 : 2, width: 22, height: 22, borderRadius: '50%', background: '#fff', transition: 'left 0.18s ease' }} />
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="form-field" style={{ marginTop: 12 }}>
                      <label style={{ marginBottom: 2, display: 'flex', alignItems: 'center', gap: 6 }}>
                        Agrupar mensagens
                        <AppIcon name="info" style={{ fontSize: '1rem', color: '#9bb3c7', cursor: 'help' }} />
                      </label>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: '#ffffff' }}>{haloForm.agruparEnabled ? 'Ativo' : 'Inativo'}</span>
                        <button type="button" role="switch" aria-checked={haloForm.agruparEnabled} onClick={() => setHaloForm(p => ({ ...p, agruparEnabled: !p.agruparEnabled }))}
                          style={{ width: 48, height: 26, borderRadius: 13, border: '1px solid var(--secondary-color)', background: haloForm.agruparEnabled ? 'var(--primary-color)' : 'rgba(0,0,0,0.25)', position: 'relative', cursor: 'pointer', padding: 0 }}>
                          <span style={{ position: 'absolute', top: 2, left: haloForm.agruparEnabled ? 24 : 2, width: 22, height: 22, borderRadius: '50%', background: '#fff', transition: 'left 0.18s ease' }} />
                        </button>
                      </div>
                      {haloForm.agruparEnabled && (
                        <div className="form-field" style={{ marginTop: 12 }}>
                          <label style={{ marginBottom: 2 }}>Tempo para agrupar (segundos)</label>
                          <input type="number" min={0} step={1} placeholder="Ex.: 30" value={haloForm.agruparSegundos || ''} onChange={e => setHaloForm(p => ({ ...p, agruparSegundos: e.target.value }))} />
                          <small style={{ color: '#9bb3c7', fontSize: '0.85rem', marginTop: '0.25rem', display: 'block' }}>0 = desabilitado</small>
                        </div>
                      )}
                    </div>

                    {/* NOVO: Contexto Inteligente */}
                    <div className="form-field" style={{ marginTop: 12 }}>
                      <label style={{ marginBottom: 2, display: 'flex', alignItems: 'center', gap: 6 }}>
                        Contexto Inteligente
                        <AppIcon name="info" style={{ fontSize: '1rem', color: '#9bb3c7', cursor: 'help' }} />
                      </label>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: '#ffffff' }}>{haloForm.contexto24h !== false ? 'Últimas 24h' : 'Sem limite'}</span>
                        <button type="button" role="switch" aria-checked={haloForm.contexto24h !== false} onClick={() => setHaloForm(p => ({ ...p, contexto24h: p.contexto24h === false ? true : false }))}
                          style={{ width: 48, height: 26, borderRadius: 13, border: '1px solid var(--secondary-color)', background: haloForm.contexto24h !== false ? 'var(--primary-color)' : 'rgba(0,0,0,0.25)', position: 'relative', cursor: 'pointer', padding: 0 }}>
                          <span style={{ position: 'absolute', top: 2, left: haloForm.contexto24h !== false ? 24 : 2, width: 22, height: 22, borderRadius: '50%', background: '#fff', transition: 'left 0.18s ease' }} />
                        </button>
                      </div>
                      <small style={{ color: '#9bb3c7', fontSize: '0.85rem', marginTop: '0.5rem', display: 'block' }}>
                        📊 Máximo: 15 mensagens | 🎯 Prioridade: 10 mais recentes | 📝 Resumo automático para conversas longas
                      </small>
                    </div>
                    {haloSaveError && (
                      <div className="info-box" style={{ borderColor: 'rgba(255,128,128,0.5)', marginTop: 12 }}>
                        <p style={{ color: '#ff8080' }}>{haloSaveError}</p>
                      </div>
                    )}
                    {haloSaveSuccess && (
                      <div className="info-box" style={{ borderColor: 'rgba(78,84,200,0.5)', marginTop: 12 }}>
                        <p style={{ color: '#a8c0d9' }}>{haloSaveSuccess}</p>
                      </div>
                    )}
                  </div>
                );
              }

              return null;
            })()}
          </div>

          {/* Rodapé com botões de navegação */}
          <div style={{ padding: '12px 16px', borderTop: '1px solid var(--secondary-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
            {/* Etapa 1: Salvar e avançar - HALO TOOLS E NANO */}
            {(modelChoice === 'pro' || modelChoice === 'nano') && haloStep === 1 && (
              <>
                <div>
                  <button type="button" className="cancel-button" onClick={() => { setModelChoice(null); setHaloStep(1); }}>
                    Voltar
                  </button>
                </div>
                <div>
                  <button
                    type="button"
                    className="save-button"
                    disabled={haloSaving}
                    onClick={async () => {
                      if (step1PostingRef.current) return;
                      step1PostingRef.current = true;
                      
                      const nome = String(haloForm.nome || '').trim();
                      if (!nome) { alert('Informe o nome do agente.'); step1PostingRef.current = false; return; }
                      if (!haloForm.funcaoPrincipal) { alert('Selecione a função principal.'); step1PostingRef.current = false; return; }
                      if (!haloForm.subFuncao) { alert('Selecione a subopção.'); step1PostingRef.current = false; return; }
                      
                      try {
                        setHaloSaveError('');
                        setHaloSaveSuccess('');
                        setHaloSaving(true);
                        
                        const empresaId = getCompanyId();
                        if (!empresaId) throw new Error('Empresa não identificada. Faça login novamente.');

                        const funcao_principal = (haloForm.funcaoPrincipal === 'atendimento') ? 'Atendimento' : 'Suporte Interno';
                        const canal = haloForm.canal;
                        const connection_id = (haloForm.canal === 'WhatsApp' && haloForm.whatsappConnectionId) ? String(haloForm.whatsappConnectionId) : null;
                        const basePrompt = buildBasePrompt({ personalidade: '', contexto: '', direcionamento: '', restricoes: '', objetivos: '', informacoesGerais: '' });

                        const body = {
                          agente_id: null,
                          company_id: empresaId,
                          connection_id,
                          canal,
                          descricao: null,
                          sub_funcao: haloForm.subFuncao || null,
                          modelo: modelChoice === 'nano' ? 'nano' : 'pro', // ⭐ Salva 'nano' ou 'pro'
                          name: nome,
                          basePrompt,
                          temperature: 0.7,
                          provider: 'openai',
                          model: modelChoice === 'nano' ? 'gpt-4o-mini' : 'gpt-4o-mini', // Nano usa gpt-4o-mini
                          funcao_principal,
                          signMessages: true,
                          readMessages: true,
                          chatbot_enabled: false,
                          chatbot_ignoreGroups: false,
                          chatbot_stopWhenYouSendMsg: 0,
                          typing_delay_seconds: 1,
                          enable_tools: true, // Habilitar ferramentas para Nano e Pro
                        };

                        // Enviando para /agents/sync
                        
                        const resp = await fetch(API_ENDPOINTS.AGENTS_SYNC, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify(body),
                        });
                        
                        if (!resp.ok) {
                          const err = await resp.json().catch(() => ({}));
                          throw new Error(err?.error || `Falha ao salvar informações iniciais (${resp.status})`);
                        }
                        
                        const data = await resp.json();
                        const created = data?.agent || null;
                        
                        if (created) {
                          setAgents(prev => {
                            const idx = prev.findIndex(a => a.id === created.id);
                            if (idx >= 0) {
                              const copy = prev.slice();
                              copy[idx] = { ...prev[idx], ...created };
                              return copy;
                            }
                            return [created, ...prev];
                          });
                          setAgenteSelecionado(created);
                        }
                        
                        setHaloSaveSuccess('Informações iniciais salvas.');
                        setHaloStep(2);
                      } catch (e) {
                        setHaloSaveError(e.message || 'Falha ao salvar.');
                      } finally {
                        setHaloSaving(false);
                        step1PostingRef.current = false;
                      }
                    }}
                  >
                    {haloSaving ? 'Salvando...' : 'Continuar'}
                  </button>
                </div>
              </>
            )}

            {/* Etapa 2: Salvar instruções e avançar - HALO TOOLS E NANO */}
            {(modelChoice === 'pro' || modelChoice === 'nano') && haloStep === 2 && (
              <>
                <div>
                  <button type="button" className="cancel-button" onClick={() => { setHaloStep(1); setHaloSaveError(''); setHaloSaveSuccess(''); }}>
                    Voltar
                  </button>
                </div>
                <div>
                  <button
                    type="button"
                    className="save-button"
                    disabled={haloSaving}
                    onClick={async () => {
                      try {
                        setHaloSaving(true);
                        setHaloSaveError('');
                        setHaloSaveSuccess('');

                        if (!agenteSelecionado?.id) throw new Error('Agente não foi criado na Etapa 1.');

                        const empresaId = getCompanyId();
                        if (!empresaId) throw new Error('Empresa não identificada.');

                        // Salvar instruções na tabela correta (halo_nano ou agentes_halo_tools)
                        const body = {
                          company_id: empresaId,
                          agente_id: agenteSelecionado.id,
                          sub_funcao: haloForm.subFuncao || null,
                          personalidade: haloForm.personalidade || null,
                          contexto: haloForm.contexto || null,
                          direcionamento: haloForm.direcionamento || null,
                          restricoes: haloForm.restricoes || null,
                          objetivos: haloForm.objetivos || null,
                          informacoes_gerais: haloForm.informacoesGerais || null,
                          descricao: haloForm.descricaoInstrucao || null,
                        };

                        // Escolhe endpoint baseado no modelo
                        const endpoint = modelChoice === 'nano' 
                          ? API_ENDPOINTS.AGENTS_NANO_UPDATE 
                          : API_ENDPOINTS.AGENTS_PRO_UPDATE;

                        const resp = await fetch(endpoint, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify(body),
                        });

                        if (!resp.ok) {
                          const err = await resp.json().catch(() => ({}));
                          throw new Error(err?.error || `Falha ao salvar instruções (${resp.status})`);
                        }

                        const result = await resp.json();

                        setHaloSaveSuccess('Instruções salvas.');
                        setHaloStep(3);
                      } catch (e) {
                        console.error('[Etapa 2] Erro:', e);
                        setHaloSaveError(e.message || 'Falha ao salvar instruções.');
                      } finally {
                        setHaloSaving(false);
                      }
                    }}
                  >
                    {haloSaving ? 'Salvando...' : 'Próximo'}
                  </button>
                </div>
              </>
            )}

            {/* Etapa 3: Salvar produtos e avançar - HALO TOOLS E NANO */}
            {(modelChoice === 'pro' || modelChoice === 'nano') && haloStep === 3 && (
              <>
                <div>
                  <button type="button" className="cancel-button" onClick={() => setHaloStep(2)}>
                    Voltar
                  </button>
                </div>
                <div>
                  <button
                    type="button"
                    className="save-button"
                    disabled={haloSaving}
                    onClick={async () => {
                      try {
                        setHaloSaving(true);
                        setHaloSaveError('');
                        setHaloSaveSuccess('');

                        if (!agenteSelecionado?.id) throw new Error('Agente não foi criado na Etapa 1.');

                        const empresaId = getCompanyId();
                        if (!empresaId) throw new Error('Empresa não identificada.');

                        // Preparar IDs das ferramentas selecionadas (salvar AMBAS se selecionadas)
                        const catalogosIds = categoriasSelecionadas.length > 0 ? categoriasSelecionadas : [];
                        const formulariosIds = formulariosSelecionados.length > 0 ? formulariosSelecionados : [];

                        const response = await fetch(API_ENDPOINTS.AGENTS_TOOLS_UPDATE_MAPPING, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            company_id: empresaId,
                            agente_id: agenteSelecionado.id,
                            catalogos_ids: catalogosIds,
                            formularios_ids: formulariosIds
                          })
                        });

                        if (!response.ok) {
                          const err = await response.json().catch(() => ({}));
                          throw new Error(err?.error || `Falha ao salvar ferramentas (${response.status})`);
                        }

                        const toolsCount = catalogosIds.length + formulariosIds.length;
                        setHaloSaveSuccess(`${toolsCount} ferramenta(s) salva(s) com sucesso!`);
                        setHaloStep(4);
                      } catch (e) {
                        setHaloSaveError(e.message || 'Falha ao salvar ferramentas.');
                      } finally {
                        setHaloSaving(false);
                      }
                    }}
                  >
                    {haloSaving ? 'Salvando...' : 'Próximo'}
                  </button>
                </div>
              </>
            )}

            {/* Etapa 4: Voltar e Concluir - HALO TOOLS E NANO */}
            {(modelChoice === 'pro' || modelChoice === 'nano') && haloStep === 4 && (
              <>
                <div>
                  <button type="button" className="cancel-button" onClick={() => setHaloStep(3)}>
                    Voltar
                  </button>
                </div>
                <div>
                  <button
                    type="button"
                    className="save-button"
                    disabled={haloSaving}
                    onClick={async () => {
                      try {
                        setHaloSaving(true);
                        setHaloSaveError('');
                        setHaloSaveSuccess('');

                        if (!agenteSelecionado?.id) throw new Error('Agente não foi criado na Etapa 1.');

                        const empresaId = getCompanyId();
                        if (!empresaId) throw new Error('Empresa não identificada.');

                        // Atualizar configurações finais
                        const waitSeconds = (haloForm.agruparEnabled ? (parseInt(haloForm.agruparSegundos, 10) || 0) : 0);
                        const body = {
                          company_id: empresaId,
                          agente_id: agenteSelecionado.id,
                          conexao_id: haloForm.whatsappConnectionId || null,
                          temperatura: haloForm.temperatura,
                          habilitar_agente: !!haloForm.agenteEnabled,
                          wait: waitSeconds,
                          contexto_24h: haloForm.contexto24h !== false, // Default true
                        };

                        // Escolhe endpoint baseado no modelo (nano ou pro)
                        // Para Etapa 4: usar UPSERT que salva temperatura e habilitar_agente
                        const endpoint = modelChoice === 'nano' 
                          ? API_ENDPOINTS.AGENTS_NANO_UPSERT 
                          : API_ENDPOINTS.AGENTS_PRO_UPSERT;

                        const resp = await fetch(endpoint, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify(body),
                        });
                        
                        if (!resp.ok) {
                          const err = await resp.json().catch(() => ({}));
                          throw new Error(err?.error || `Falha ao salvar configurações (${resp.status})`);
                        }

                        setHaloSaveSuccess('Agente criado com sucesso! Carregando...');
                        setIsRefreshingAfterCreate(true);
                        
                        // Aguardar um pouco para garantir que o backend processou
                        await new Promise(resolve => setTimeout(resolve, 1500));
                        
                        // Recarregar agentes
                        await carregarAgentes();
                        setIsRefreshingAfterCreate(false);
                        
                        // Fechar modal e resetar formulário
                        setTimeout(() => {
                          setModalAberto(false);
                          setModelChoice(null);
                          setHaloStep(1);
                          // Resetar formulário
                          setHaloForm({
                            nome: '',
                            descricao: '',
                            canal: 'Interno',
                            whatsappConnectionId: '',
                            funcaoPrincipal: '',
                            subFuncao: '',
                            temperatura: 0.7,
                            modeloIa: 'gpt-4o-mini',
                            wait_text: '',
                            personalidade: '',
                            contexto: '',
                            direcionamento: '',
                            restricoes: '',
                            objetivos: '',
                            informacoesGerais: '',
                            descricaoInstrucao: '',
                            agenteEnabled: true,
                            agruparEnabled: false,
                            agruparSegundos: '',
                          });
                          setHaloSaveSuccess('');
                          setHaloSaveError('');
                        }, 500);
                      } catch (e) {
                        setHaloSaveError(e.message || 'Falha ao concluir.');
                      } finally {
                        setHaloSaving(false);
                      }
                    }}
                  >
                    {haloSaving ? 'Salvando...' : 'Concluir'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ============================================================================
  // RENDER PRINCIPAL
  // ============================================================================

  if (initialLoading) {
    return (
      <div className="agentes-container">
        <LoadingSpinner message="Carregando agentes..." />
      </div>
    );
  }

  return (
    <div className="agentes-container" ref={containerRef}>
      {renderSidebar()}
      
      {/* Divisor arrastável */}
      <div
        className="split-handle"
        onMouseDown={onResizeMouseDown}
        role="separator"
        aria-orientation="vertical"
        aria-label="Redimensionar lista de agentes"
      />
      
      <section className="agente-main">
        {renderMainArea()}
      </section>

      {/* Novo Modal Step-by-Step */}
      <AgentCreatorStepModal
        isOpen={novoModalAberto}
        onClose={() => {
          setNovoModalAberto(false);
          setAgenteParaEditar(null);
        }}
        companyId={getCompanyId()}
        userId={user?.id}
        editingAgent={agenteParaEditar}
        onComplete={(agente) => {
          setNovoModalAberto(false);
          setAgenteParaEditar(null);
          // Recarregar lista de agentes
          loadAgents();
        }}
      />

      {/* Modal Antigo Conversacional (comentado, não deletado) */}
      {/* <AgentCreatorModal
        isOpen={novoModalAberto}
        onClose={() => setNovoModalAberto(false)}
        companyId={getCompanyId()}
        userId={user?.id}
        onComplete={(data) => {
          setNovoModalAberto(false);
        }}
      /> */}

      {renderModal()}
      
      {/* Loading overlay quando carregando agentes ou após criar */}
      {(loadingAgents || isRefreshingAfterCreate) && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          backdropFilter: 'blur(4px)'
        }}>
          <LoadingSpinner message={isRefreshingAfterCreate ? 'Finalizando agente...' : 'Carregando agentes...'} />
        </div>
      )}

      {/* Sub-Modal de Geração com IA */}
      {showAiGeneratorModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1100
          }}
          onClick={() => {
            setShowAiGeneratorModal(false);
            setAiDescricaoFuncao('');
            setAiGeneratorError('');
          }}
        >
          <div
            style={{
              width: '90%',
              maxWidth: 600,
              background: 'var(--card-background)',
              border: '2px solid #44e1ff',
              borderRadius: 16,
              boxShadow: '0 20px 60px rgba(68, 225, 255, 0.3)',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{
              padding: '1.5rem',
              background: 'linear-gradient(135deg, rgba(68, 225, 255, 0.1), rgba(180, 59, 234, 0.1))',
              borderBottom: '1px solid rgba(68, 225, 255, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <AppIcon name="auto_awesome" style={{ fontSize: '2rem', color: '#44e1ff' }} />
                <h3 style={{ margin: 0, color: '#ffffff', fontSize: '1.5rem' }}>Geração Automática com IA</h3>
              </div>
              <button
                onClick={() => {
                  setShowAiGeneratorModal(false);
                  setAiDescricaoFuncao('');
                  setAiGeneratorError('');
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#ffffff',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  padding: '0.25rem'
                }}
              >
                ×
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: '1.5rem' }}>
              {/* Explicação */}
              <div style={{
                background: 'rgba(68, 225, 255, 0.05)',
                border: '1px solid rgba(68, 225, 255, 0.2)',
                borderRadius: 12,
                padding: '1rem',
                marginBottom: '1.5rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                  <AppIcon name="lightbulb" style={{ color: '#44e1ff', fontSize: '1.5rem' }} />
                  <div>
                    <h4 style={{ margin: '0 0 0.5rem 0', color: '#44e1ff', fontSize: '1rem' }}>Como funciona:</h4>
                    <p style={{ margin: 0, color: '#9bb3c7', fontSize: '0.9rem', lineHeight: 1.6 }}>
                      Nossa IA irá analisar as configurações da sua empresa e as informações do agente 
                      para gerar automaticamente todos os campos de instruções de forma otimizada e personalizada.
                    </p>
                  </div>
                </div>
              </div>

              {/* Campo de descrição */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  color: '#ffffff',
                  fontWeight: 600,
                  fontSize: '0.95rem'
                }}>
                  📝 Descreva a função principal deste agente:
                </label>
                <textarea
                  value={aiDescricaoFuncao}
                  onChange={(e) => setAiDescricaoFuncao(e.target.value)}
                  placeholder="Ex: Atender clientes via WhatsApp, apresentar cardápio, tirar dúvidas sobre ingredientes, processar pedidos e informar tempo de entrega"
                  rows={5}
                  disabled={aiGeneratorLoading}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: 'rgba(15, 12, 41, 0.6)',
                    border: '1px solid rgba(68, 225, 255, 0.3)',
                    borderRadius: 8,
                    color: '#ffffff',
                    fontSize: '0.9rem',
                    resize: 'vertical',
                    fontFamily: 'inherit',
                    lineHeight: 1.5
                  }}
                />
                <small style={{ display: 'block', marginTop: '0.5rem', color: '#9bb3c7', fontSize: '0.85rem' }}>
                  💡 Dica: Seja específico sobre o que o agente deve fazer no dia a dia para obter melhores resultados.
                </small>
              </div>

              {/* Erro */}
              {aiGeneratorError && (
                <div style={{
                  padding: '0.75rem 1rem',
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: 8,
                  color: '#fca5a5',
                  marginBottom: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <AppIcon name="error" />
                  {aiGeneratorError}
                </div>
              )}

              {/* Loading */}
              {aiGeneratorLoading && (
                <div style={{
                  padding: '1.5rem',
                  textAlign: 'center',
                  background: 'rgba(68, 225, 255, 0.05)',
                  borderRadius: 12,
                  marginBottom: '1rem'
                }}>
                  <AppIcon name="sync" className="spinning" style={{
                    fontSize: '2.5rem',
                    color: '#44e1ff',
                    marginBottom: '0.75rem',
                    display: 'block'
                  }} />
                  <p style={{ margin: '0 0 0.5rem 0', color: '#ffffff', fontWeight: 600 }}>
                    Gerando instruções...
                  </p>
                  <p style={{ margin: 0, color: '#9bb3c7', fontSize: '0.9rem' }}>
                    Analisando configurações da empresa...<br />
                    Processando informações do agente...<br />
                    Gerando instruções otimizadas...
                  </p>
                  <p style={{ margin: '0.75rem 0 0 0', color: '#9bb3c7', fontSize: '0.85rem' }}>
                    Isso pode levar alguns segundos...
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={{
              padding: '1rem 1.5rem',
              background: 'rgba(15, 12, 41, 0.4)',
              borderTop: '1px solid rgba(68, 225, 255, 0.2)',
              display: 'flex',
              justifyContent: 'space-between',
              gap: '1rem'
            }}>
              <button
                onClick={() => {
                  setShowAiGeneratorModal(false);
                  setAiDescricaoFuncao('');
                  setAiGeneratorError('');
                }}
                disabled={aiGeneratorLoading}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: 'transparent',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: 8,
                  color: '#ffffff',
                  cursor: aiGeneratorLoading ? 'not-allowed' : 'pointer',
                  fontSize: '0.95rem',
                  opacity: aiGeneratorLoading ? 0.5 : 1
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handleGenerateWithAI}
                disabled={aiGeneratorLoading || !aiDescricaoFuncao.trim()}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: aiGeneratorLoading || !aiDescricaoFuncao.trim() 
                    ? 'rgba(68, 225, 255, 0.3)' 
                    : 'linear-gradient(135deg, #44e1ff, #b43bea)',
                  border: 'none',
                  borderRadius: 8,
                  color: '#ffffff',
                  fontWeight: 600,
                  cursor: aiGeneratorLoading || !aiDescricaoFuncao.trim() ? 'not-allowed' : 'pointer',
                  fontSize: '0.95rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  boxShadow: aiGeneratorLoading || !aiDescricaoFuncao.trim() 
                    ? 'none' 
                    : '0 4px 12px rgba(68, 225, 255, 0.3)'
                }}
              >
                <AppIcon name="auto_awesome" />
                {aiGeneratorLoading ? 'Gerando...' : 'Gerar com IA'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
